import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Container, Form, Button, InputGroup, Spinner, Alert, Modal, ButtonGroup, Row, Col, Badge } from 'react-bootstrap';
import axios from 'axios';
import axiosInstance from '../api/axiosInstance';
import { useReactMediaRecorder } from "react-media-recorder";
import { FaStop, FaMicrophone, FaTimes, FaMusic, FaImage, FaFont, FaHeadphones, FaBullseye, FaEye, FaEyeSlash, FaClock, FaArrowUp, FaArrowDown, FaFolderOpen, FaPlus, FaBalanceScale, FaTrash, FaBars, FaCopy, FaFilePdf, FaFileImage, FaRobot, FaShieldAlt } from "react-icons/fa";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useAuthStore from '../stores/authStore';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const getExtension = (lang) => {
    switch (lang.toLowerCase()) {
        case 'javascript': case 'js': return 'js';
        case 'typescript': case 'ts': return 'ts';
        case 'python': case 'py': return 'py';
        case 'html': return 'html';
        case 'css': return 'css';
        case 'json': return 'json';
        case 'java': return 'java';
        case 'c': return 'c';
        case 'cpp': return 'cpp';
        case 'csharp': case 'cs': return 'cs';
        case 'go': return 'go';
        case 'rust': case 'rs': return 'rs';
        case 'bash': case 'sh': return 'sh';
        default: return 'txt';
    }
};

const CodeBlock = ({ node, inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    const codeString = String(children).replace(/\n$/, '');
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(codeString);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy code: ', err);
        }
    };

    const handleDownload = () => {
        const blob = new Blob([codeString], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const ext = getExtension(language);
        a.download = `code_${Date.now()}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    if (inline) {
        return (
            <code className={className} {...props}>
                {children}
            </code>
        );
    }

    return (
        <div className="custom-code-block-container">
            <div className="code-block-header">
                <span className="code-block-language">{language || 'code'}</span>
                <div className="code-block-actions" data-html2canvas-ignore="true">
                    <button className="code-block-action-btn" onClick={handleDownload} title="파일로 다운로드">
                        <FaArrowDown size={12} />
                    </button>
                    <button className="code-block-action-btn" onClick={handleCopy} title="코드 복사">
                        {copied ? '복사 완료! ✔️' : <FaCopy size={12} />}
                    </button>
                </div>
            </div>
            <pre className="code-block-pre">
                <code className={className} {...props}>
                    {children}
                </code>
            </pre>
        </div>
    );
};

const categoryEmojis = {
    '화풍/스타일': '🎨',
    '인물/피사체': '👤',
    '행동': '🏃',
    '배경': '🏞️',
    '조명': '💡',
    '카메라앵글': '📸'
};

const parseImagePrompt = (text) => {
    const lines = text.split('\n');
    const result = [];
    lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) return;
        const match = trimmed.match(/^\*\*(.*?)\*\*:\s*(.*)/) || trimmed.match(/^(.*?):\s*(.*)/);
        if (match) {
            result.push({ label: match[1].trim(), value: match[2].trim() });
        }
    });
    return result;
};

const Agent = () => {
    // 가사 직접 입력 예시 텍스트
    const LYRIC_PLACEHOLDER = `반드시 아래 양식에 맞게 입력해주세요.
[intro]
내 말 좀 들어봐
이제 시작해볼게

[verse]
아침 햇살이 눈을 깨우고
새로운 하루가 시작돼

[chorus]
꿈을 향해 달려갈 거야
저 높은 하늘 끝까지

[bridge]
가끔은 넘어져도 괜찮아
다시 일어나면 되니까

[outro]
영원히 기억할게
우리의 이 노래를`;

    // === 상태 관리 변수 ===
    const [inputText, setInputText] = useState(''); // 사용자가 입력하는 텍스트
    const [messages, setMessages] = useState([]); // 채팅 메시지 목록
    const [isLoading, setIsLoading] = useState(false); // 로딩 상태 (API 요청 중인지 여부)

    // === 사용자 세션/인증 연동 ===
    const { isAuthenticated: isLoggedIn, user } = useAuthStore();

    // === 대화방 세션 상태 관리 ===
    const [sessions, setSessions] = useState([]); // 대화방 목록
    const [currentSessionId, setCurrentSessionId] = useState(''); // 현재 선택된 세션 ID
    const isNewSessionRef = useRef(false);
    const [copiedIndex, setCopiedIndex] = useState(null); // 최근 복사된 메시지 인덱스
    const [activeMenuIndex, setActiveMenuIndex] = useState(null); // 활성화된 저장/복사 메뉴 인덱스
    const [headerMenuOpen, setHeaderMenuOpen] = useState(false); // 헤더의 전체 대화 저장 메뉴 열림 여부
    const [allCopied, setAllCopied] = useState(false); // 전체 대화 복사 완료 피드백 상태
    const [mode, setMode] = useState('text'); // 모드 관리: 'text', 'image', 'voice', 'music'
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768); // 사이드바 오픈 여부 (데스크톱만 기본 오픈)
    const [isSessionsLoading, setIsSessionsLoading] = useState(false); // 세션 로딩 여부

    // UUID 생성 함수 (v4)
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (Math.random() * 16) | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
            return v.toString(16);
        });
    };

    // currentSessionId Ref to prevent infinite loops / unnecessary recreation of fetchSessions
    const currentSessionIdRef = useRef(currentSessionId);
    useEffect(() => {
        currentSessionIdRef.current = currentSessionId;
    }, [currentSessionId]);

    useEffect(() => {
        // 모바일 환경에서 페이지 바디 스크롤을 막고 챗 내부 스크롤만 활성화
        const originalStyle = document.body.style.overflow;
        if (window.innerWidth <= 768) {
            window.scrollTo(0, 0);
            document.body.scrollTop = 0;
            if (document.documentElement) {
                document.documentElement.scrollTop = 0;
            }
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = originalStyle;
        };
    }, []);

    // 대화방 목록 조회 API 호출
    const fetchSessions = useCallback(async (activeIdToSelect = null) => {
        setIsSessionsLoading(true);
        try {
            let res;
            if (isLoggedIn && user?.email) {
                res = await axiosInstance.post('/llm-user', {
                    reqType: 'member',
                    user_email: user.email
                });
            } else {
                const anonSessions = JSON.parse(localStorage.getItem('wablog_anonymous_sessions') || '[]');
                if (anonSessions.length > 0) {
                    res = await axiosInstance.post('/llm-user', {
                        reqType: 'nomember',
                        uuid: anonSessions
                    });
                } else {
                    setSessions([]);
                    setIsSessionsLoading(false);
                    return;
                }
            }

            if (res && res.data && res.data.status === '0000') {
                const list = res.data.data.results || [];
                setSessions(list);

                if (list.length > 0) {
                    if (activeIdToSelect && list.some(s => s.id === activeIdToSelect)) {
                        setCurrentSessionId(activeIdToSelect);
                        const matched = list.find(s => s.id === activeIdToSelect);
                        if (matched && (matched.mode === 'text' || matched.mode === 'rag' || matched.mode === 'insurance')) {
                            setMode(matched.mode);
                        }
                    } else if (!currentSessionIdRef.current) {
                        setCurrentSessionId(list[0].id);
                        if (list[0].mode === 'text' || list[0].mode === 'rag' || list[0].mode === 'insurance') {
                            setMode(list[0].mode);
                        }
                    }
                }
            }
        } catch (err) {
            console.error('대화 세션 목록 로드 실패:', err);
        } finally {
            setIsSessionsLoading(false);
        }
    }, [isLoggedIn, user]);

    // 특정 대화방 상세 메시지 조회 API 호출
    const loadSessionMessages = useCallback(async (sessionId) => {
        if (!sessionId) return;
        setIsLoading(true);
        setError('');
        try {
            const res = await axiosInstance.post('/llm-user', {
                reqType: 'msgdetail',
                session_id: sessionId
            });

            if (res && res.data && res.data.status === '0000') {
                const list = res.data.data.results || [];
                const formatted = list.map(msg => ({
                    text: msg.content,
                    sender: msg.sender,
                    type: 'text',
                    duration: msg.duration
                }));
                setMessages(formatted);
            } else {
                setMessages([]);
            }
        } catch (err) {
            console.error('메시지 로드 실패:', err);
            setError('이전 대화 내용을 불러오는 중 오류가 발생했습니다.');
            setMessages([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // 로그인 유저의 대화방 로드 및 동기화
    useEffect(() => {
        fetchSessions();
    }, [isLoggedIn, user?.email, fetchSessions]);

    // 활성화된 대화방 변경 시 메시지 내역 불러오기
    useEffect(() => {
        if (currentSessionId) {
            if (isNewSessionRef.current) {
                isNewSessionRef.current = false;
                setMessages([]);
            } else {
                loadSessionMessages(currentSessionId);
            }
        } else {
            setMessages([]);
        }
    }, [currentSessionId, loadSessionMessages]);

    // 탭(모드) 변경 시, 현재 선택된 대화방의 모드와 일치하지 않으면 자동 세션 전환 또는 새 대화 시작
    useEffect(() => {
        if (mode === 'text' || mode === 'rag' || mode === 'insurance') {
            const currentSession = sessions.find(s => s.id === currentSessionId);

            if ((currentSession && currentSession.mode !== mode) || !currentSessionId) {
                const sameModeSessions = sessions.filter(s => s.mode === mode);
                if (sameModeSessions.length > 0) {
                    setCurrentSessionId(sameModeSessions[0].id);
                } else {
                    const newId = generateUUID();
                    isNewSessionRef.current = true;
                    setCurrentSessionId(newId);
                    setMessages([]);
                    setError('');
                }
            }
        }
    }, [mode, sessions, currentSessionId]);

    // 새 대화방 시작 핸들러
    const handleNewChat = () => {
        const newId = generateUUID();
        isNewSessionRef.current = true;
        setCurrentSessionId(newId);
        setMessages([]);
        setError('');
    };

    // 대화방 삭제 핸들러
    const handleDeleteSession = async (e, sessionId) => {
        e.stopPropagation();
        if (!window.confirm("이 대화방을 삭제하시겠습니까?")) return;

        try {
            const res = await axiosInstance.post('/llm-user', {
                reqType: 'del',
                session_id: sessionId
            });

            if (res && res.data && res.data.status === '0000') {
                if (!isLoggedIn) {
                    const anonSessions = JSON.parse(localStorage.getItem('wablog_anonymous_sessions') || '[]');
                    const filtered = anonSessions.filter(id => id !== sessionId);
                    localStorage.setItem('wablog_anonymous_sessions', JSON.stringify(filtered));
                }

                if (currentSessionId === sessionId) {
                    setMode('text');
                    handleNewChat();
                }
                fetchSessions();
            }
        } catch (err) {
            console.error('대화방 삭제 실패:', err);
            setError('대화방 삭제 중 오류가 발생했습니다.');
        }
    };

    // 이미지 생성 모드: 'text' (텍스트 입력), 'random' (랜덤/선택)
    const [imageInputMode, setImageInputMode] = useState('text');
    // 이미지 생성 모델: 'image' (z-image, 기본값), 'imageQwen' (qwen image 2.1)
    const [imageModel, setImageModel] = useState('image');

    // 음악 생성 모드 상태
    const [musicComposition, setMusicComposition] = useState({
        lyricsSegments: [], // { structure: '[intro]', text: '...' }
        tags: [], // 'Pop', 'Guitar', etc.
        language: 'Korean', // 기본값 한국어
        duration: 120, // 기본값 120초 (60~240)
        bpm: 60 // 기본값 60 (40~200)
    });

    // 음악 스타일 옵션 상태 (API Fetch)
    const [musicOptions, setMusicOptions] = useState({
        genres: [],
        instruments: [],
        vocals: [],
        moods: []
    });

    // 현재 가사 입력 상태
    const [lyricInputState, setLyricInputState] = useState({
        structure: '선택', // 선택, [intro], [verse], ...
        text: ''
    });
    // 현재 태그 선택 상태
    const [tagInputState, setTagInputState] = useState({
        genre: '',
        vocal: '',
        instrument: '',
        mood: ''
    });

    // 랜덤 프롬프트 옵션 데이터
    const [promptOptions, setPromptOptions] = useState({
        medium: [],
        subject: [],
        action: [],
        background: [],
        lighting: [],
        camera: []
    });

    // 사용자가 선택한 옵션 값
    // 초기값은 모두 'None' (사용안함) (요구사항 변경: 2025-12-15)
    // Random 옵션이 삭제되었으므로 초기값은 None이어야 함.
    const [selectedOptions, setSelectedOptions] = useState({
        medium: 'None',
        subject: 'None',
        action: 'None',
        background: 'None',
        lighting: 'None',
        camera: 'None'
    });

    // 직접입력(Prompt) 선택 시 입력된 텍스트
    const [manualInputs, setManualInputs] = useState({
        medium: '',
        subject: '',
        action: '',
        background: '',
        lighting: '',
        camera: ''
    });

    const [error, setError] = useState(''); // 오류 메시지

    // 이미지 크게 보기를 위한 상태
    const [showLargeImage, setShowLargeImage] = useState(false); // 큰 이미지 모달 표시 여부
    const [currentImage, setCurrentImage] = useState({ src: '', fileName: '' }); // 현재 크게 볼 이미지 정보

    // 채팅창 자동 스크롤을 위한 ref
    const messagesEndRef = useRef(null);

    // n8n 웹훅 URL 및 타임아웃 시간
    const WEBHOOK_URL_CHAT = "/ai/llm-chatting";
    const WEBHOOK_URL_VOICE = "/ai/voice";
    const TIMEOUT_DURATION = 300000; // 300초

    // 가사 생성 모달 상태
    const [showLyricsModal, setShowLyricsModal] = useState(false);
    const [lyricsPrompt, setLyricsPrompt] = useState('');
    const [generatedLyrics, setGeneratedLyrics] = useState('');
    const [isGeneratingLyrics, setIsGeneratingLyrics] = useState(false);
    const [isComposerExpanded, setIsComposerExpanded] = useState(true); // 작곡 UI 펼침/접힘 상태
    const [isRandomImageExpanded, setIsRandomImageExpanded] = useState(true); // 랜덤 이미지 UI 펼침/접힘 상태
    const [processingTime, setProcessingTime] = useState(0); // 응답 대기 시간 (초)

    // 가사 생성 API 호출 핸들러
    const handleGenerateLyrics = async () => {
        if (!lyricsPrompt.trim()) {
            setError("가사 주제와 내용을 입력해주세요.");
            return;
        }

        setIsGeneratingLyrics(true);
        setError('');

        try {
            const params = {
                lyricsDesc: lyricsPrompt,
                genres: musicComposition.tags.join(', '),
                seconds: musicComposition.duration,
                bpm: musicComposition.bpm
            };

            const response = await axiosInstance.post('ai/llm-gen-lyrics', params);

            if (response.data && response.data.status === '0000') {
                setGeneratedLyrics(response.data.lyrics);
            } else {
                setError('가사 생성에 실패했습니다. 다시 시도해주세요.');
            }
        } catch (err) {
            console.error(err);
            setError('가사 생성 중 오류가 발생했습니다.');
        } finally {
            setIsGeneratingLyrics(false);
        }
    };

    // 생성된 가사 적용 핸들러
    const handleApplyGeneratedLyrics = () => {
        if (!generatedLyrics.trim()) return;

        // 직접 입력 파싱 로직 재사용
        const parts = generatedLyrics.split(/(\[.*?\])/).filter(p => p.trim() !== '');

        const segments = [];
        let currentStruct = '';

        parts.forEach(part => {
            const trimmed = part.trim();
            if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                currentStruct = trimmed;
            } else if (currentStruct) {
                segments.push({ structure: currentStruct, text: trimmed });
            }
        });

        if (segments.length > 0) {
            // 기존 가사 초기화 후 적용 (요구사항: 기존 가사 삭제)
            setMusicComposition(prev => ({
                ...prev,
                lyricsSegments: segments
            }));

            // 모달 닫기 및 초기화
            setShowLyricsModal(false);
            setLyricsPrompt('');
            setGeneratedLyrics('');
        } else {
            setError("가사 형식을 확인해주세요. (예: [intro] 내용...)");
        }
    };

    // 모달 닫기 핸들러 (초기화 포함)
    const handleCloseLyricsModal = () => {
        setShowLyricsModal(false);
        setLyricsPrompt('');
        setGeneratedLyrics('');
    };

    // 음성 녹음 Hook
    const { startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } = useReactMediaRecorder({ audio: true });
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const timerRef = useRef(null);
    const isCancelledRef = useRef(false); // 녹음 취소 여부 확인용 ref
    const lastSentBlobUrlRef = useRef(null); // 중복 전송 방지용 ref
    const fileInputRef = useRef(null); // 파일 업로드 input ref
    const inputRef = useRef(null); // 입력창 포커스용 ref
    const containerRef = useRef(null); // 컨테이너 스크롤 제어용 ref

    // 로딩 완료, 탭 전환, 또는 대화방 전환 시 입력창에 자동으로 포커스 주기 (데스크톱에서만)
    useEffect(() => {
        if (window.innerWidth > 768 && !isLoading && inputRef.current && !inputRef.current.disabled) {
            inputRef.current.focus();
        }
    }, [isLoading, mode, currentSessionId]);

    // 입력창 텍스트 변경 시 높이 자동 조절 (Gemini 스타일 멀티라인 대응)
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 180)}px`;
        }
    }, [inputText]);

    // 메시지 내용 복사 핸들러
    const handleCopyMessage = async (text, index) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedIndex(index);
            setTimeout(() => {
                setCopiedIndex(null);
            }, 1500);
            setTimeout(() => {
                setActiveMenuIndex(null); // 1초 대기 후 메뉴 닫기 (피드백 노출용)
            }, 1000);
        } catch (err) {
            console.error('클립보드 복사 실패:', err);
        }
    };

    // PDF 저장 핸들러
    const handleSaveAsPDF = async (index) => {
        const element = document.getElementById(`msg-bubble-${index}`);
        if (!element) return;

        setIsLoading(true);
        setError('');
        try {
            // CSS 스타일 유지를 위한 임시 래퍼 컨테이너 생성
            const wrapper = document.createElement('div');
            wrapper.className = 'agent-messages-display';
            wrapper.style.height = 'auto';
            wrapper.style.maxHeight = 'none';
            wrapper.style.overflow = 'visible';
            wrapper.style.position = 'absolute';
            wrapper.style.top = '-9999px';
            wrapper.style.left = '-9999px';
            wrapper.style.width = element.parentElement.clientWidth + 'px';
            wrapper.style.padding = '0'; // 패딩 제거하여 콤팩트 캡처

            const clone = element.cloneNode(true);
            wrapper.appendChild(clone);
            document.body.appendChild(wrapper);

            const canvas = await html2canvas(clone, {
                useCORS: true,
                allowTaint: true,
                scale: 2, // 고해상도
                backgroundColor: null // 투명 배경 혹은 CSS 배경색 유지
            });

            document.body.removeChild(wrapper);

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: canvas.width > canvas.height ? 'l' : 'p',
                unit: 'px',
                format: [canvas.width / 2, canvas.height / 2] // 2배 스케일 원복
            });

            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
            pdf.save(`chat_message_${index + 1}.pdf`);
            setActiveMenuIndex(null);
        } catch (err) {
            console.error('PDF 저장 실패:', err);
            setError('PDF 파일 생성 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 이미지 저장 핸들러
    const handleSaveAsPNG = async (index) => {
        const element = document.getElementById(`msg-bubble-${index}`);
        if (!element) return;

        setIsLoading(true);
        setError('');
        try {
            const wrapper = document.createElement('div');
            wrapper.className = 'agent-messages-display';
            wrapper.style.height = 'auto';
            wrapper.style.maxHeight = 'none';
            wrapper.style.overflow = 'visible';
            wrapper.style.position = 'absolute';
            wrapper.style.top = '-9999px';
            wrapper.style.left = '-9999px';
            wrapper.style.width = element.parentElement.clientWidth + 'px';
            wrapper.style.padding = '0';

            const clone = element.cloneNode(true);
            wrapper.appendChild(clone);
            document.body.appendChild(wrapper);

            const canvas = await html2canvas(clone, {
                useCORS: true,
                allowTaint: true,
                scale: 2, // 고해상도
                backgroundColor: null // 투명 배경 혹은 CSS 배경색 유지
            });

            document.body.removeChild(wrapper);

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `chat_message_${index + 1}.png`;
            link.click();
            setActiveMenuIndex(null);
        } catch (err) {
            console.error('이미지 저장 실패:', err);
            setError('이미지 파일 생성 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 전체 대화 복사 핸들러 (마크다운)
    const handleCopyAllMarkdown = async () => {
        if (messages.length === 0) return;

        const markdown = messages.map(msg => {
            const senderName = msg.sender === 'user' ? 'User' : 'AI';
            if (msg.type === 'image') {
                return `**[${senderName}]**: ![생성된 이미지](${msg.text})`;
            } else if (msg.type === 'audio') {
                return `**[${senderName}]**: [생성된 음성/음악](${msg.text})`;
            } else {
                return `**[${senderName}]**:\n${msg.text}`;
            }
        }).join('\n\n---\n\n');

        try {
            await navigator.clipboard.writeText(markdown);
            setAllCopied(true);
            setTimeout(() => {
                setAllCopied(false);
            }, 1500);
            setTimeout(() => {
                setHeaderMenuOpen(false); // 메뉴 닫기
            }, 1000);
        } catch (err) {
            console.error('전체 클립보드 복사 실패:', err);
        }
    };

    // 전체 대화 PDF 저장 핸들러
    const handleSaveAllAsPDF = async () => {
        const element = document.getElementById('agent-messages-display');
        if (!element || messages.length === 0) return;

        setIsLoading(true);
        setError('');
        try {
            // CSS 스타일 유지를 위해 부모 클래스 컨테이너 래핑 및 오버플로우 해제
            const wrapper = document.createElement('div');
            wrapper.className = 'agent-chat-card';
            wrapper.style.position = 'absolute';
            wrapper.style.top = '-9999px';
            wrapper.style.left = '-9999px';
            wrapper.style.width = element.parentElement.clientWidth + 'px';

            const clone = element.cloneNode(true);
            clone.style.height = 'auto';
            clone.style.maxHeight = 'none';
            clone.style.overflow = 'visible';

            wrapper.appendChild(clone);
            document.body.appendChild(wrapper);

            const canvas = await html2canvas(clone, {
                useCORS: true,
                allowTaint: true,
                scale: 2,
                backgroundColor: '#ffffff' // 전체 저장은 흰색 배경
            });

            document.body.removeChild(wrapper);

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: canvas.width > canvas.height ? 'l' : 'p',
                unit: 'px',
                format: [canvas.width / 2, canvas.height / 2]
            });

            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
            pdf.save(`chat_session_full.pdf`);
            setHeaderMenuOpen(false);
        } catch (err) {
            console.error('전체 대화 PDF 저장 실패:', err);
            setError('전체 대화 PDF 파일 생성 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 전체 대화 이미지 저장 핸들러
    const handleSaveAllAsPNG = async () => {
        const element = document.getElementById('agent-messages-display');
        if (!element || messages.length === 0) return;

        setIsLoading(true);
        setError('');
        try {
            const wrapper = document.createElement('div');
            wrapper.className = 'agent-chat-card';
            wrapper.style.position = 'absolute';
            wrapper.style.top = '-9999px';
            wrapper.style.left = '-9999px';
            wrapper.style.width = element.parentElement.clientWidth + 'px';

            const clone = element.cloneNode(true);
            clone.style.height = 'auto';
            clone.style.maxHeight = 'none';
            clone.style.overflow = 'visible';

            wrapper.appendChild(clone);
            document.body.appendChild(wrapper);

            const canvas = await html2canvas(clone, {
                useCORS: true,
                allowTaint: true,
                scale: 2,
                backgroundColor: '#ffffff'
            });

            document.body.removeChild(wrapper);

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `chat_session_full.png`;
            link.click();
            setHeaderMenuOpen(false);
        } catch (err) {
            console.error('전체 대화 이미지 저장 실패:', err);
            setError('전체 대화 이미지 파일 생성 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 채팅창을 가장 아래로 스크롤하는 함수
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // 녹음 타이머 관리
    useEffect(() => {
        if (isRecording) {
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
            setRecordingTime(0);
        }
        return () => clearInterval(timerRef.current);
    }, [isRecording]);

    // 랜덤 프롬프트 옵션 가져오기
    useEffect(() => {
        const fetchPromptOptions = async () => {
            try {
                const response = await axiosInstance.post('ai/llm-random-prompt');
                if (response.data && response.data.status === '0000' && response.data.promptList) {
                    setPromptOptions(response.data.promptList);
                } else {
                    console.error('Failed to fetch prompt options:', response.data);
                }
            } catch (err) {
                console.error('Error fetching prompt options:', err);
            }
        };

        const fetchMusicOptions = async () => {
            try {
                const response = await axiosInstance.post('/ai/llm-composition-style');
                if (response.data && response.data.status === '0000' && response.data.styleList) {
                    setMusicOptions(response.data.styleList);
                } else {
                    console.error('Failed to fetch music options:', response.data);
                }
            } catch (err) {
                console.error('Error fetching music options:', err);
            }
        };

        fetchPromptOptions();
        fetchMusicOptions();
    }, []);

    // 응답 대기 타이머
    useEffect(() => {
        let interval;
        if (isLoading) {
            setProcessingTime(0);
            const startTime = Date.now();
            interval = setInterval(() => {
                setProcessingTime((Date.now() - startTime) / 1000);
            }, 100);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isLoading]);

    // 메시지 전송 처리 함수 (텍스트/이미지/음악)
    const handleSendMessage = async (e, overrideOptions = null) => {
        const startTime = Date.now();
        if (e) e.preventDefault();

        // 1. 유효성 검사
        if (mode === 'voice') {
            // 음성 모드는 별도 로직
        } else if (mode === 'image' && imageInputMode === 'random') {
            // 랜덤 이미지 모드: 입력값 검사 패스
        } else if (mode === 'music') {
            // 작곡 모드: 가사나 태그 중 하나라도 있어야 함
            if (musicComposition.lyricsSegments.length === 0 && musicComposition.tags.length === 0) {
                setError("가사나 태그를 입력해주세요! 🎵");
                return;
            }
        } else {
            // 텍스트/일반 이미지 모드 / RAG 모드
            if (!inputText.trim()) return;
        }

        const textToSend = inputText.trim();
        const isWeatherQuery = (mode === 'text' || mode === 'rag') && (
            textToSend.includes('날씨') ||
            textToSend.includes('기온') ||
            textToSend.includes('기상') ||
            textToSend.includes('비와') ||
            textToSend.includes('눈와') ||
            textToSend.includes('weather') ||
            textToSend.includes('temperature')
        );
        let displayMessage = '';
        let params = { reqType: 'chatting' };

        // 텍스트 또는 RAG 모드일 경우 대화방 ID 확보
        let activeSessionId = currentSessionId;
        if (mode === 'text' || mode === 'rag' || mode === 'insurance') {
            if (!activeSessionId) {
                activeSessionId = generateUUID();
                isNewSessionRef.current = true;
                setCurrentSessionId(activeSessionId);
            }
            // 비회원일 경우 로컬 스토리지에 세션 ID 추가 (새 대화 버튼을 통해 ID가 선발급된 경우도 저장 보장)
            if (!isLoggedIn) {
                const anonSessions = JSON.parse(localStorage.getItem('wablog_anonymous_sessions') || '[]');
                if (!anonSessions.includes(activeSessionId)) {
                    anonSessions.push(activeSessionId);
                    localStorage.setItem('wablog_anonymous_sessions', JSON.stringify(anonSessions));
                }
            }
        }

        // 텍스트 모드이고 날씨 질문이 아닐 때 첫 메시지 전송 시 대화방 생성(upsert) 및 사용자 메시지 등록(newmsg) 선행
        if (mode === 'text' && !isWeatherQuery) {
            try {
                if (messages.length === 0) {
                    await axiosInstance.post('/llm-user', {
                        reqType: 'upsert',
                        session_id: activeSessionId,
                        user_email: isLoggedIn ? user.email : null,
                        title: textToSend.substring(0, 30) + (textToSend.length > 30 ? '...' : ''),
                        mode: 'text'
                    });
                }

                await axiosInstance.post('/llm-user', {
                    reqType: 'newmsg',
                    session_id: activeSessionId,
                    sender: 'user',
                    content: textToSend,
                    duration: 0.0
                });
            } catch (dbErr) {
                console.error("❌ 대화방/사용자 질문 DB 저장 중 에러 발생:", dbErr);
            }
        }

        // 기존 로직과 병합된 형태 (전체 구조 재정의)
        // **작곡 모드 로직 (NEW)**
        if (mode === 'music') {
            params.reqType = 'song';

            // 가사 포맷팅: [intro] \n text \n\n [verse] ...
            const formattedLyrics = musicComposition.lyricsSegments
                .map(seg => seg.text && seg.text.trim() ? `${seg.structure}\n${seg.text}` : seg.structure)
                .join('\n\n');


            params.lyrics = formattedLyrics;
            params.language = musicComposition.language; // 언어 추가
            params.duration = musicComposition.duration; // 길이 추가

            // BPM 태그 정의 (복구)
            const bpmTag = `${musicComposition.bpm} bpm`;

            // 채팅창에 보여줄 메시지 (프리뷰와 비슷하지만 X버튼 없는 텍스트)
            // 마크다운 이용해서 예쁘게 꾸밈
            const lyricsDisplay = musicComposition.lyricsSegments
                .map(seg => `**${seg.structure}**\n${seg.text}`)
                .join('\n\n');

            // 태그 표시용 (텍스트 사용)
            const displayTags = musicComposition.tags.length > 0
                ? `${musicComposition.tags.map(t => t.text).join(', ')}, ${bpmTag}`
                : bpmTag;

            // 실제 전송용 태그 (Value 사용)
            const formattedTags = musicComposition.tags.length > 0
                ? `${musicComposition.tags.map(t => t.value).join(', ')}, ${bpmTag}`
                : bpmTag;

            params.tags = formattedTags;

            displayMessage = `🎵 **작곡 요청** (${musicComposition.language === 'Korean' ? '한국어' : musicComposition.language}, ${musicComposition.duration}초, ${musicComposition.bpm} BPM)\n\n${lyricsDisplay}${displayTags ? `\n\n**Tags**: ${displayTags}` : ''}`;

            // 프리뷰 버블 제거 (전송됨)
            setMusicComposition({ lyricsSegments: [], tags: [], language: 'Korean', duration: 120, bpm: 60 });
            // 입력창 초기화
            setLyricInputState({ structure: '선택', text: '' });
            setTagInputState({ genre: '', vocal: '', instrument: '', mood: '' });

        } else if (mode === 'image' && imageInputMode === 'random') {
            // Random 모드 (기존 유지)
            params.reqType = imageModel;
            const inputData = { userPrompt: {} };
            const currentOptions = overrideOptions || selectedOptions;
            const categoryOrder = ['medium', 'subject', 'action', 'background', 'lighting', 'camera'];
            let promptDetails = [];

            categoryOrder.forEach(category => {
                const selected = currentOptions[category];
                let resolvedText = '';
                if (selected === 'None') { inputData[category] = 'None'; resolvedText = '사용안함'; }
                else if (selected === 'Prompt') {
                    inputData[category] = 'Prompt';
                    const val = manualInputs[category];
                    if (val?.trim()) { inputData.userPrompt[category] = val.trim(); resolvedText = `직접입력: ${val.trim()}`; }
                    else { resolvedText = '직접입력 (내용 없음)'; }
                }
                else if (selected != null && selected !== '') {
                    const idx = parseInt(selected, 10);
                    if (promptOptions[category] && promptOptions[category][idx]) { inputData[category] = idx; resolvedText = promptOptions[category][idx]; }
                    else { inputData[category] = idx; resolvedText = `옵션 #${idx}`; }
                }
                else { inputData[category] = 'Random'; resolvedText = '무작위'; }
                promptDetails.push(`**${categoryLabels[category]}**: ${resolvedText}`);
            });
            params.inputData = inputData;
            params.inputText = '';
            displayMessage = promptDetails.join('  \n');

        } else if (mode === 'image') {
            // 텍스트 이미지 모드
            params.reqType = imageModel;
            params.inputText = textToSend;
            displayMessage = textToSend;
        } else if (mode === 'rag' || mode === 'insurance' || isWeatherQuery) {
            // RAG 모드, 보험상담 모드 또는 날씨 질문의 경우 파라미터 구성 (FastAPI로 전달)
            params = {
                query: textToSend,
                session_id: activeSessionId,
                user_email: isLoggedIn ? user.email : null,
                mode: mode
            };
            displayMessage = textToSend;
        } else {
            // 텍스트 채팅 모드
            params.reqType = 'chatting';

            // 이전 대화 내역(최근 6개)을 n8n prompt 컨텍스트에 바인딩
            const historyText = messages
                .slice(-6)
                .map(m => `${m.sender === 'user' ? 'User' : 'AI'}: ${m.text}`)
                .join('\n');
            const fullPrompt = messages.length > 0
                ? `[이전 대화 내역]\n${historyText}\n\n[현재 질문]\n${textToSend}`
                : textToSend;

            params.inputText = fullPrompt;
            displayMessage = textToSend;
        }

        // 사용자 메시지 추가
        const userMessage = { text: displayMessage, sender: 'user', type: 'text' };
        setMessages((prevMessages) => [...prevMessages, userMessage]);

        setInputText('');
        setError('');
        setIsLoading(true);

        try {
            // responseType 설정: 이미지나 노래는 blob 필요
            const responseType = (mode === 'image' || mode === 'music') ? 'arraybuffer' : 'json';

            let response;
            if (mode === 'rag' || mode === 'insurance' || isWeatherQuery) {
                // RAG 및 보험상담 모드, 날씨 질문은 FastAPI 백엔드로 직접 POST 전송하며, 30분 타임아웃을 지정합니다.
                response = await axios.post('https://agent.wajok.com/api/chat', params, {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    timeout: 1800000 // 30분
                });
            } else {
                response = await axiosInstance.post(WEBHOOK_URL_CHAT, params, {
                    responseType: responseType,
                    timeout: 1800000 // 30분으로 연장
                });
            }

            if (mode === 'image') {
                const contentType = response.headers['content-type'];
                if (contentType && (contentType.startsWith('image/') || contentType.includes('image'))) {
                    const mimeType = contentType.split(';')[0].trim() || 'image/png';
                    const ext = mimeType.includes('jpeg') || mimeType.includes('jpg') ? 'jpg' : (mimeType.includes('webp') ? 'webp' : 'png');
                    const blob = new Blob([response.data], { type: mimeType });
                    const imageUrl = URL.createObjectURL(blob);
                    const duration = ((Date.now() - startTime) / 1000).toFixed(1);
                    setMessages(prev => [...prev, { text: imageUrl, sender: 'bot', type: 'image', fileName: `generated_image.${ext}`, duration }]);
                } else {
                    // 에러 텍스트 디코딩
                    const textDecoder = new TextDecoder('utf-8');
                    const textData = textDecoder.decode(response.data);
                    setMessages(prev => [...prev, { text: '오류: ' + textData, sender: 'bot', type: 'text' }]);
                }
            } else if (mode === 'music') {
                // 음악 응답 처리 (audio/mpeg)
                const contentType = response.headers['content-type'];
                // contentType 확인 (오디오인지)
                if (contentType && (contentType.includes('audio') || contentType.includes('mpeg'))) {
                    const blob = new Blob([response.data], { type: 'audio/mpeg' }); // mp3
                    const audioUrl = URL.createObjectURL(blob);
                    const duration = ((Date.now() - startTime) / 1000).toFixed(1);
                    setMessages(prev => [...prev, { text: audioUrl, sender: 'bot', type: 'audio', fileName: 'generated_music.mp3', duration }]);
                } else {
                    const textDecoder = new TextDecoder('utf-8');
                    const textData = textDecoder.decode(response.data);
                    setMessages(prev => [...prev, { text: '생성 실패: ' + textData, sender: 'bot', type: 'text' }]);
                }
            } else if (mode === 'rag' || mode === 'insurance' || isWeatherQuery) {
                // RAG 및 날씨 응답 처리 (포스트맨 스크린샷 기준 answer에 마크다운이 담겨서 옴)
                let outputText = "응답을 처리할 수 없습니다.";
                if (response.data) {
                    const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
                    outputText = data.answer || "응답을 처리할 수 없습니다.";
                }
                const duration = ((Date.now() - startTime) / 1000).toFixed(1);
                setMessages(prev => [...prev, { text: outputText, sender: 'bot', type: 'text', duration }]);

                // 대화방 목록 갱신
                fetchSessions(activeSessionId);
            } else {
                // 텍스트 응답
                const outputText = response.data.data?.output || response.data.output || "응답을 처리할 수 없습니다.";
                const duration = ((Date.now() - startTime) / 1000).toFixed(1);
                setMessages(prev => [...prev, { text: outputText, sender: 'bot', type: 'text', duration }]);

                // 텍스트 모드인 경우 봇 답변도 DB에 기록하고 세션 정보 업데이트
                try {
                    await axiosInstance.post('/llm-user', {
                        reqType: 'newmsg',
                        session_id: activeSessionId,
                        sender: 'bot',
                        content: outputText,
                        duration: parseFloat(duration)
                    });

                    await axiosInstance.post('/llm-user', {
                        reqType: 'timeup',
                        session_id: activeSessionId
                    });

                    // 대화방 목록 갱신 및 유지
                    fetchSessions(activeSessionId);
                } catch (dbErr) {
                    console.error("❌ 봇 답변 DB 저장 중 에러 발생:", dbErr);
                }
            }
        } catch (err) {
            console.error(err);
            let errorMessage = '';
            if (axios.isCancel(err) || err.code === 'ECONNABORTED') {
                errorMessage = (mode === 'rag' || mode === 'insurance')
                    ? '서버 응답 시간이 초과되었습니다 (10분). 잠시 후 다시 시도해주세요. ⏳'
                    : '서버 응답 시간이 초과되었습니다 (5분). 잠시 후 다시 시도해주세요. ⏳';
            } else if (err.message && err.message.includes('Network Error')) {
                errorMessage = '네트워크 오류가 발생했습니다. 서버 연결 상태나 인터넷 연결을 확인해주세요. 🌐';
            } else {
                errorMessage = '오류 발생: ' + (err.response?.data?.message || err.message);
            }
            setError(errorMessage);

            // RAG, 보험상담 모드 및 날씨 질문에서 오류 발생 시 화면의 텍스트에어리어에 오류 메시지를 추가하여 다시 질문해달라고 안내
            if (mode === 'rag' || mode === 'insurance' || isWeatherQuery) {
                setMessages(prev => [...prev, {
                    text: '⚠️ 오류가 발생하여 답변을 생성하지 못했습니다. 다시 질문해 주세요.',
                    sender: 'bot',
                    type: 'text'
                }]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 음성 녹음 시작/중지 및 전송 핸들러
    const handleMicClick = async () => {
        if (!isRecording) {
            // 녹음 시작
            isCancelledRef.current = false; // 취소 플래그 초기화
            setIsRecording(true);
            startRecording();
        } else {
            // 녹음 중지 및 전송
            setIsRecording(false);
            stopRecording();
        }
    };

    // 녹음 취소 핸들러
    const handleCancelRecording = () => {
        isCancelledRef.current = true; // 취소 플래그 설정
        setIsRecording(false);
        stopRecording();
        setRecordingTime(0);
    };

    // 파일 업로드 핸들러
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const blobUrl = URL.createObjectURL(file);
        sendVoiceMessage(blobUrl);

        // input 초기화 (같은 파일 다시 선택 가능하도록)
        e.target.value = '';
    };

    // mediaBlobUrl이 업데이트되면(녹음이 완료되면) 자동으로 전송
    // 단, 사용자가 의도적으로 녹음을 멈춘 경우(isRecording이 false로 변한 직후)에만 전송해야 함.
    // 이를 위해 별도의 플래그나 로직이 필요할 수 있음.
    // 여기서는 간단히 mediaBlobUrl이 생기면 바로 전송하는 로직을 구현하되,
    // 초기 렌더링 시 실행되지 않도록 주의.
    const sendVoiceMessage = useCallback(async (blobUrl) => {
        setIsLoading(true);
        setError('');

        // 사용자 메시지(음성) 표시 (여기서는 텍스트로 '음성 메시지 전송됨' 표시하거나 오디오 플레이어 표시)
        // 요구사항에는 명시되지 않았으나, 사용자 경험상 보낸 음성도 들을 수 있으면 좋음.
        setMessages((prev) => [...prev, { text: blobUrl, sender: 'user', type: 'audio' }]);

        try {
            const blob = await fetch(blobUrl).then(r => r.blob());
            const formData = new FormData();
            formData.append('data', blob, 'recording.wav'); // n8n 워크플로우에서 'data' 필드 사용

            const response = await axiosInstance.post(WEBHOOK_URL_VOICE, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                responseType: 'blob', // 음성 파일 응답 예상
                timeout: TIMEOUT_DURATION
            });

            // 응답 처리 (음성 파일)
            const audioUrl = URL.createObjectURL(response.data);
            setMessages((prev) => [...prev, { text: audioUrl, sender: 'bot', type: 'audio' }]);

        } catch (err) {
            console.error(err);
            setError('음성 전송/응답 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
            clearBlobUrl(); // 메모리 해제
        }
    }, [clearBlobUrl]);

    // mediaBlobUrl이 업데이트되면(녹음이 완료되면) 자동으로 전송
    // 단, 사용자가 의도적으로 녹음을 멈춘 경우(isRecording이 false로 변한 직후)에만 전송해야 함.
    // 이를 위해 별도의 플래그나 로직이 필요할 수 있음.
    // 여기서는 간단히 mediaBlobUrl이 생기면 바로 전송하는 로직을 구현하되,
    // 초기 렌더링 시 실행되지 않도록 주의.
    const isMount = useRef(true);
    useEffect(() => {
        if (isMount.current) {
            isMount.current = false;
            return;
        }
        if (mediaBlobUrl && !isRecording && mode === 'voice') {
            if (lastSentBlobUrlRef.current === mediaBlobUrl) {
                return;
            }
            if (isCancelledRef.current) {
                // 취소된 경우 전송하지 않고 blobUrl만 정리
                clearBlobUrl();
                isCancelledRef.current = false;
                lastSentBlobUrlRef.current = null;
            } else {
                lastSentBlobUrlRef.current = mediaBlobUrl;
                sendVoiceMessage(mediaBlobUrl);
            }
        }
    }, [mediaBlobUrl, isRecording, mode, clearBlobUrl, sendVoiceMessage]);

    // 이미지 모드 변경 함수 (텍스트 / 무작위)
    const handleImageModeChange = (targetMode) => {
        if (imageInputMode === targetMode) return;

        if (targetMode === 'random') {
            setInputText(''); // 텍스트 -> 무작위 전환 시 입력값 초기화
        }
        setImageInputMode(targetMode);
    };

    // 옵션 변경 핸들러
    const handleOptionChange = (category, value) => {
        setSelectedOptions(prev => ({
            ...prev,
            [category]: value
        }));
    };

    // 직접 입력 변경 핸들러
    const handleManualInputChange = (category, value) => {
        setManualInputs(prev => ({
            ...prev,
            [category]: value
        }));
    };

    // 카테고리 한글 명칭 매핑
    const categoryLabels = {
        medium: '화풍/스타일',
        subject: '인물/피사체',
        action: '행동',
        background: '배경',
        lighting: '조명',
        camera: '카메라앵글'
    };

    // 카테고리 순서
    const categoryOrder = ['medium', 'subject', 'action', 'background', 'lighting', 'camera'];

    // 룰렛 애니메이션 및 전송 핸들러 ("쏘세요!" 버튼)
    const handleShootRoulette = async () => {
        // 1. 유효성 검사 (직접입력 확인)
        for (const category of categoryOrder) {
            if (selectedOptions[category] === 'Prompt' && !manualInputs[category]?.trim()) {
                setError(`${categoryLabels[category]} 항목의 내용을 입력해주세요! ✍️`);
                return;
            }
        }

        // 2. 룰렛 대상 카테고리 선정
        // 수정: 'Prompt' (직접입력)가 아닌 모든 항목은 다시 룰렛을 돌립니다.
        // (None이든, 이전에 선택된 값이든 상관없이)
        const categoriesToSpin = categoryOrder.filter(cat => selectedOptions[cat] !== 'Prompt');

        // 최소 선택 개수 체크
        // (Prompt 개수) + (돌릴 개수) >= 2
        // 사실상 돌릴 개수가 0이 아닌 이상, 혹은 Prompt가 2개 이상이면 OK.
        // 근데 Prompt 아닌 건 다 돌리니까, Prompt + Spin = 6. 무조건 6 >= 2.
        // 다만 Prompt 아닌 게 하나도 없으면(사용자가 다 직접입력함) -> Spin=0. Prompt가 2개 미만이면 Error.
        const manualPromptCount = categoryOrder.filter(cat => selectedOptions[cat] === 'Prompt' && manualInputs[cat]?.trim()).length;
        if (categoriesToSpin.length === 0 && manualPromptCount < 2) {
            setError("최소 2개 이상의 옵션을 직접 입력하거나, 룰렛을 돌릴 항목을 남겨주세요.");
            return;
        }

        setError('');
        setIsLoading(true);

        // 최종 전송할 옵션 객체 (현재 상태 복사)
        const finalOptions = { ...selectedOptions };

        // 순차적 애니메이션 실행
        for (const category of categoriesToSpin) {
            // 옵션 목록 가져오기
            const options = promptOptions[category];
            if (!options || options.length === 0) continue;

            // 룰렛 돌리고 결과 값 받기
            const resultIdx = await spinCategory(category, options);
            finalOptions[category] = resultIdx.toString(); // 결과 반영
        }

        // 애니메이션 종료 후 잠시 대기
        await new Promise(r => setTimeout(r, 500));

        // 수정된 triggerImageGeneration 호출
        triggerImageGeneration(finalOptions);
    };

    // 가사 세그먼트 추가 핸들러
    const handleAddLyric = () => {
        const { structure, text } = lyricInputState;

        if (structure === '직접입력' && text) {
            // 직접 입력 파싱 로직
            // 정규식: 대괄호로 묶인 구조 [structure] 를 찾아서 분리
            const parts = text.split(/(\[.*?\])/).filter(p => p.trim() !== '');

            const segments = [];
            let currentStruct = '';

            parts.forEach(part => {
                const trimmed = part.trim();
                // 대괄호로 둘러싸여 있으면 구조로 인식
                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                    currentStruct = trimmed;
                } else if (currentStruct) {
                    // 구조가 잡힌 상태에서 텍스트가 나오면 세그먼트 생성
                    segments.push({ structure: currentStruct, text: trimmed });
                }
            });

            if (segments.length > 0) {
                setMusicComposition(prev => ({
                    ...prev,
                    lyricsSegments: [...prev.lyricsSegments, ...segments]
                }));
                // 입력창 초기화
                setLyricInputState({ structure: '선택', text: '' });
            } else {
                // 파싱 실패 시 경고
                setError("가사 형식을 확인해주세요. (예: [intro] 내용...)");
            }

        } else if (structure !== '선택') {
            // 기존 개별 추가 로직
            // 텍스트가 비어있어도 구조만 추가 가능하도록 (Instrumental 등 고려) - 기존 로직 유지
            // 하지만 보통 가사가 있어야 함. 일단 기존 로직 따름.

            setMusicComposition(prev => ({
                ...prev,
                lyricsSegments: [...prev.lyricsSegments, { structure, text }]
            }));

            // 입력창 초기화 (구조는 '선택'으로, 텍스트는 비우기)
            setLyricInputState({ structure: '선택', text: '' });
        }
    };

    // 가사 세그먼트 삭제 핸들러
    const handleRemoveLyric = (index) => {
        setMusicComposition(prev => ({
            ...prev,
            lyricsSegments: prev.lyricsSegments.filter((_, i) => i !== index)
        }));
    };

    // 가사 세그먼트 순서 이동 핸들러
    const handleMoveLyric = (index, direction) => {
        const newSegments = [...musicComposition.lyricsSegments];
        if (direction === 'up') {
            if (index === 0) return;
            [newSegments[index - 1], newSegments[index]] = [newSegments[index], newSegments[index - 1]];
        } else if (direction === 'down') {
            if (index === newSegments.length - 1) return;
            [newSegments[index + 1], newSegments[index]] = [newSegments[index], newSegments[index + 1]];
        }
        setMusicComposition(prev => ({ ...prev, lyricsSegments: newSegments }));
    };

    // 태그 추가 핸들러 (수정: value와 text 모두 저장)
    const handleAddTag = (value, text, type) => {
        if (!value) return;

        // 이미 존재하는지 체크
        if (musicComposition.tags.some(t => t.value === value)) {
            // 이미 있으면 무시하고 선택값만 초기화
            setTagInputState(prev => ({ ...prev, [type]: '' }));
            return;
        }

        setMusicComposition(prev => ({
            ...prev,
            tags: [...prev.tags, { value, text }]
        }));

        // 선택값 초기화
        setTagInputState(prev => ({ ...prev, [type]: '' }));
    };

    // 태그 삭제 핸들러
    const handleRemoveTag = (tagValue) => {
        setMusicComposition(prev => ({
            ...prev,
            tags: prev.tags.filter(t => t.value !== tagValue)
        }));
    };

    const triggerImageGeneration = (options) => {
        // handleSendMessage 호출 (옵션 전달)
        handleSendMessage({ preventDefault: () => { } }, options);
    };

    // 특정 카테고리 룰렛 돌리기 (값 반환하도록 수정)
    const spinCategory = async (category, options) => {
        return new Promise(resolve => {
            let count = 0;
            const maxSpins = 10;
            const interval = 50;
            let finalIdx = 0;

            const spin = setInterval(() => {
                const randomIdx = Math.floor(Math.random() * options.length);
                finalIdx = randomIdx;

                // 화면 업데이트 (시각적 효과)
                setSelectedOptions(prev => ({
                    ...prev,
                    [category]: randomIdx.toString()
                }));
                count++;

                if (count >= maxSpins) {
                    clearInterval(spin);
                    resolve(finalIdx); // 최종 인덱스 반환
                }
            }, interval);
        });
    };
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // 이미지를 클릭했을 때 크게 보기 모달 열기
    const handleImageClick = (src, fileName) => {
        setCurrentImage({ src, fileName });
        setShowLargeImage(true);
    };

    // 이미지 크게 보기 모달 닫기
    const handleCloseLargeImage = () => {
        setShowLargeImage(false);
        setCurrentImage({ src: '', fileName: '' });
    };

    // 시간 포맷팅 (MM:SS)
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return mins.toString().padStart(2, '0') + ':' + secs.toString().padStart(2, '0') + ' ';
    };

    const handleContainerScrollReset = () => {
        if (containerRef.current) {
            containerRef.current.scrollTop = 0;
        }
    };

    return (
        <Container
            className="agent-container"
            ref={containerRef}
            onScroll={handleContainerScrollReset}
        >
            {/* 대화 세션 및 사이드바용 인라인 스타일 */}
            <style>
                {`
                    /* 에이전트 레이아웃 개편 */
                    .agent-container {
                        max-width: 1200px !important;
                        margin: 0 auto;
                        padding: 1.5rem 1rem !important;
                        height: calc(100vh - 120px);
                        display: flex;
                        flex-direction: column;
                    }
                    .agent-layout {
                        display: flex;
                        flex: 1;
                        height: 100% !important; /* 고정 780px 해제 */
                        min-height: 680px;
                        background: rgba(255, 255, 255, 0.85);
                        backdrop-filter: blur(20px);
                        -webkit-backdrop-filter: blur(20px);
                        border-radius: 24px;
                        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
                        border: 1px solid rgba(255, 255, 255, 0.6);
                        overflow: hidden;
                        margin-top: 0.5rem;
                        position: relative;
                    }
                    
                    /* 사이드바 스타일 */
                    .agent-sidebar {
                        width: 280px;
                        background: #0f172a;
                        color: #ffffff;
                        display: flex;
                        flex-direction: column;
                        border-right: 1px solid rgba(255, 255, 255, 0.06);
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        z-index: 10;
                    }
                    .agent-sidebar.collapsed {
                        width: 0px;
                        overflow: hidden;
                        border-right: none;
                    }
                    .sidebar-header {
                        padding: 1.5rem 1.25rem;
                        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .sidebar-title {
                        font-weight: 700;
                        font-size: 0.95rem;
                        color: #f1f5f9;
                        margin: 0;
                    }
                    .new-chat-btn {
                        background: linear-gradient(135deg, #6366f1, #4f46e5);
                        border: none;
                        border-radius: 20px;
                        color: white;
                        font-weight: 600;
                        font-size: 0.8rem;
                        padding: 0.5rem 1rem;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        transition: all 0.2s ease;
                        box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
                    }
                    .new-chat-btn:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 6px 16px rgba(79, 70, 229, 0.4);
                        background: linear-gradient(135deg, #4f46e5, #4338ca);
                    }
                    .session-list {
                        flex: 1;
                        overflow-y: auto;
                        padding: 0.75rem 0.5rem;
                    }
                    .session-list::-webkit-scrollbar {
                        width: 4px;
                    }
                    .session-list::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.1);
                        border-radius: 2px;
                    }
                    .session-item {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 0.75rem 1rem;
                        border-radius: 12px;
                        margin-bottom: 0.35rem;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        color: #94a3b8;
                    }
                    .session-item:hover {
                        background: rgba(255, 255, 255, 0.04);
                        color: #f1f5f9;
                    }
                    .session-item.active {
                        background: rgba(255, 255, 255, 0.08);
                        color: #ffffff;
                        font-weight: 600;
                        box-shadow: inset 0 0 1px 1px rgba(255,255,255,0.05);
                    }
                    .session-title {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        flex: 1;
                        font-size: 0.85rem;
                        text-align: left;
                    }
                    .session-delete-btn {
                        color: #64748b;
                        background: transparent;
                        border: none;
                        padding: 4px 6px;
                        border-radius: 6px;
                        transition: all 0.2s ease;
                        font-size: 0.8rem;
                        opacity: 0;
                    }
                    .session-item:hover .session-delete-btn {
                        opacity: 1;
                    }
                    .session-delete-btn:hover {
                        color: #ef4444;
                        background: rgba(239, 68, 68, 0.15);
                    }
                    
                    /* 대화 말풍선 복사 버튼 스타일 */
                    .agent-message-bubble {
                        position: relative !important;
                    }
                    .copy-bubble-btn {
                        position: absolute;
                        top: 8px;
                        right: 8px;
                        opacity: 0;
                        background: rgba(255, 255, 255, 0.9);
                        border: 1px solid rgba(0, 0, 0, 0.06);
                        color: #64748b;
                        cursor: pointer;
                        transition: opacity 0.2s ease, background 0.2s ease, color 0.2s ease;
                        padding: 5px;
                        border-radius: 6px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
                        z-index: 5;
                    }
                    .agent-message-bubble:hover .copy-bubble-btn {
                        opacity: 1;
                    }
                    .copy-bubble-btn:hover {
                        background: #ffffff;
                        color: #4f46e5;
                        box-shadow: 0 2px 8px rgba(79, 70, 229, 0.15);
                    }
                    .agent-message-bubble.user .copy-bubble-btn {
                        background: rgba(0, 0, 0, 0.2);
                        color: rgba(255, 255, 255, 0.85);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        box-shadow: none;
                    }
                    .agent-message-bubble.user .copy-bubble-btn:hover {
                        background: rgba(0, 0, 0, 0.35);
                        color: #ffffff;
                    }

                    /* 복사/저장 드롭다운 메뉴 스타일 */
                    .copy-dropdown-menu {
                        position: absolute;
                        top: 36px;
                        right: 8px;
                        background: rgba(255, 255, 255, 0.95);
                        border: 1px solid rgba(0, 0, 0, 0.08);
                        border-radius: 10px;
                        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                        z-index: 999;
                        min-width: 140px;
                        padding: 6px 0;
                        backdrop-filter: blur(10px);
                    }
                    .agent-message-bubble.user .copy-dropdown-menu {
                        background: rgba(30, 30, 36, 0.95);
                        border: 1px solid rgba(255, 255, 255, 0.15);
                    }
                    .copy-dropdown-item {
                        padding: 8px 12px;
                        font-size: 0.8rem;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        color: #334155;
                        transition: background 0.15s ease, color 0.15s ease;
                        text-align: left;
                        width: 100%;
                        background: none;
                        border: none;
                    }
                    .copy-dropdown-item:hover {
                        background: #f1f5f9;
                        color: #4f46e5;
                    }
                    .agent-message-bubble.user .copy-dropdown-item {
                        color: rgba(255, 255, 255, 0.85);
                    }
                    .agent-message-bubble.user .copy-dropdown-item:hover {
                        background: rgba(255, 255, 255, 0.1);
                        color: #ffffff;
                    }

                    /* 헤더용 드롭다운 메뉴 스타일 */
                    .header-dropdown-menu {
                        position: absolute;
                        top: 36px;
                        right: 0;
                        left: auto !important;
                        background: rgba(255, 255, 255, 0.95);
                        border: 1px solid rgba(0, 0, 0, 0.08);
                        border-radius: 10px;
                        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                        z-index: 999;
                        min-width: 145px;
                        padding: 6px 0;
                        backdrop-filter: blur(10px);
                    }
                    .header-dropdown-item {
                        padding: 8px 12px;
                        font-size: 0.8rem;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        color: #334155;
                        transition: background 0.15s ease, color 0.15s ease;
                        text-align: left;
                        width: 100%;
                        background: none;
                        border: none;
                    }
                    .header-dropdown-item:hover {
                        background: #f1f5f9;
                        color: #4f46e5;
                    }

                    /* 마크다운 스타일 커스텀 */
                    .agent-message-bubble pre {
                        background: #0f172a;
                        color: #f8fafc;
                        padding: 12px 16px;
                        border-radius: 10px;
                        overflow-x: auto;
                        font-family: 'Fira Code', Consolas, monospace;
                        font-size: 0.85rem;
                        margin: 10px 0;
                    }
                    .agent-message-bubble code {
                        font-family: 'Fira Code', Consolas, monospace;
                        font-size: 0.85rem;
                        background: rgba(0, 0, 0, 0.05);
                        padding: 2px 4px;
                        border-radius: 4px;
                        color: #e11d48;
                    }
                    .agent-message-bubble.user code {
                        background: rgba(255, 255, 255, 0.2);
                        color: #ffffff;
                    }

                    /* 커스텀 코드 블록 (Gemini 스타일) */
                    .custom-code-block-container {
                        background: #1e1e2e !important;
                        border-radius: 12px;
                        overflow: hidden;
                        margin: 14px 0;
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    }
                    .code-block-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        background: #181825;
                        padding: 8px 16px;
                        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                    }
                    .code-block-language {
                        font-size: 0.78rem;
                        color: #cdd6f4;
                        font-family: 'Fira Code', Consolas, monospace;
                        font-weight: 600;
                        text-transform: capitalize;
                    }
                    .code-block-actions {
                        display: flex;
                        gap: 8px;
                        align-items: center;
                    }
                    .code-block-action-btn {
                        background: transparent;
                        border: none;
                        color: #a6adc8;
                        cursor: pointer;
                        padding: 4px 8px;
                        border-radius: 6px;
                        font-size: 0.72rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s ease;
                        font-family: inherit;
                    }
                    .code-block-action-btn:hover {
                        background: rgba(255, 255, 255, 0.1);
                        color: #ffffff;
                    }
                    .custom-code-block-container pre {
                        background: transparent !important;
                        margin: 0 !important;
                        padding: 16px !important;
                        border-radius: 0 !important;
                    }
                    .custom-code-block-container code {
                        background: transparent !important;
                        padding: 0 !important;
                        color: #a6e3a1 !important; /* Gemini 느낌의 연녹색/허브그린 코드 컬러 기조 */
                        font-family: 'Fira Code', Consolas, monospace;
                        font-size: 0.85rem;
                        line-height: 1.5;
                        text-shadow: none !important;
                    }
                    .agent-message-bubble table {
                        border-collapse: collapse;
                        width: 100%;
                        margin: 12px 0;
                        font-size: 0.85rem;
                        text-align: left;
                        background-color: #ffffff;
                        border-radius: 8px;
                        overflow: hidden;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                    }
                    .agent-message-bubble th, .agent-message-bubble td {
                        border: 1px solid #e2e8f0;
                        padding: 8px 12px;
                    }
                    .agent-message-bubble th {
                        background-color: #f1f5f9;
                        font-weight: 600;
                        color: #1e293b;
                    }
                    .agent-message-bubble tr:nth-child(even) {
                        background-color: #f8fafc;
                    }
                    
                    /* 메인 채팅창 스타일 */
                    .agent-chat-card {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        background: transparent;
                        border: none;
                        box-shadow: none;
                        border-radius: 0;
                    }
                    
                    /* 사이드바 토글 버튼 */
                    .sidebar-toggle-btn {
                        background: transparent;
                        border: none;
                        color: #475569;
                        padding: 8px;
                        border-radius: 8px;
                        transition: all 0.2s ease;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                    }
                    .sidebar-toggle-btn:hover {
                        background: #f1f5f9;
                        color: #0f172a;
                    }

                    /* 헤더 */
                    .agent-header {
                        padding: 1.25rem 1.5rem;
                        background: #ffffff;
                        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                    }
                    .agent-title {
                        font-weight: 700;
                        font-size: 1.15rem;
                        color: #0f172a;
                    }

                    /* 메시지 디스플레이 영역 */
                    .agent-messages-display {
                        flex-grow: 1;
                        overflow-y: auto;
                        padding: 2rem 2.5rem;
                        display: flex;
                        flex-direction: column;
                        gap: 16px;
                        background-color: #f8fafc;
                        scrollbar-width: thin;
                        scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
                    }
                    .agent-messages-display::-webkit-scrollbar {
                        width: 6px;
                    }
                    .agent-messages-display::-webkit-scrollbar-thumb {
                        background: rgba(0, 0, 0, 0.1);
                        border-radius: 3px;
                    }
                    .agent-messages-display::-webkit-scrollbar-track {
                        background: transparent;
                    }

                    /* 말풍선 스타일 개편 */
                    .agent-message-bubble {
                        max-width: 80%;
                        padding: 14px 20px;
                        border-radius: 20px;
                        font-size: 0.95rem;
                        line-height: 1.55;
                        word-wrap: break-word;
                        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
                        border: 1px solid transparent;
                        position: relative;
                        animation: fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
                    }
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(12px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .agent-message-bubble.user {
                        align-self: flex-end;
                        background: linear-gradient(135deg, #6366f1, #4f46e5);
                        color: #ffffff;
                        border-bottom-right-radius: 4px;
                        box-shadow: 0 8px 24px rgba(79, 70, 229, 0.15);
                    }
                    
                    .agent-message-bubble.bot {
                        align-self: flex-start;
                        background: #ffffff;
                        color: #1e293b;
                        border: 1px solid rgba(0, 0, 0, 0.04);
                        border-bottom-left-radius: 4px;
                        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
                    }
                    
                    .agent-message-bubble p {
                        margin-bottom: 0.5rem;
                    }
                    .agent-message-bubble p:last-child {
                        margin-bottom: 0;
                    }

                    .agent-message-bubble.bot.is-loading {
                        background: #ffffff;
                        color: #64748b;
                        display: flex;
                        align-items: center;
                        font-weight: 500;
                    }

                    /* 이미지 썸네일 */
                    .agent-image-thumbnail-container {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 8px;
                    }
                    .agent-image-thumbnail {
                        max-width: 200px;
                        height: auto;
                        border-radius: 12px;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                        cursor: pointer;
                        transition: transform 0.2s ease-in-out;
                    }
                    .agent-image-thumbnail:hover {
                        transform: scale(1.03);
                    }
                    .agent-image-download-thumbnail a {
                        font-size: 0.85rem;
                        color: #4f46e5;
                        text-decoration: none;
                        font-weight: 600;
                    }

                    /* 사이드바 모바일 닫기 버튼 스타일 */
                    .sidebar-close-mobile-btn {
                        display: flex !important;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255, 255, 255, 0.1) !important;
                        border: 1px solid rgba(255, 255, 255, 0.15) !important;
                        color: rgba(255, 255, 255, 0.7) !important;
                        padding: 6px !important;
                        border-radius: 50% !important;
                        transition: all 0.2s ease !important;
                        cursor: pointer !important;
                        width: 28px !important;
                        height: 28px !important;
                        outline: none !important;
                        box-shadow: none !important;
                        margin-left: 4px !important;
                    }
                    .sidebar-close-mobile-btn:hover {
                        background: rgba(255, 255, 255, 0.2) !important;
                        color: #ffffff !important;
                        border-color: rgba(255, 255, 255, 0.3) !important;
                    }
                    
                    @media (min-width: 1025px) {
                        .sidebar-close-mobile-btn {
                            display: none !important;
                        }
                    }

                    /* 프리미엄 웰컴 카드 스타일 */
                    .agent-welcome-card {
                        background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(243, 244, 246, 0.9));
                        border: 1px solid rgba(99, 102, 241, 0.15);
                        border-radius: 24px;
                        padding: 2.5rem 2rem;
                        max-width: 600px;
                        text-align: center;
                        box-shadow: 0 10px 30px rgba(99, 102, 241, 0.05);
                        margin: 2rem auto;
                        animation: fadeInUp 0.5s ease-out;
                    }
                    .welcome-icon-wrapper {
                        width: 64px;
                        height: 64px;
                        border-radius: 20px;
                        background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(99, 102, 241, 0.2));
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin: 0 auto 1.25rem auto;
                        box-shadow: 0 8px 20px rgba(99, 102, 241, 0.1);
                    }
                    .welcome-icon {
                        color: #4f46e5;
                    }
                    .welcome-icon.rag {
                        color: #6366f1;
                    }
                    .welcome-title {
                        font-size: 1.35rem;
                        font-weight: 700;
                        color: #1e293b;
                        margin-bottom: 0.75rem;
                        letter-spacing: -0.02em;
                    }
                    .welcome-subtitle {
                        font-size: 0.95rem;
                        color: #64748b;
                        line-height: 1.6;
                        margin-bottom: 1.5rem;
                    }
                    .welcome-divider {
                        height: 1px;
                        background: rgba(99, 102, 241, 0.1);
                        width: 80%;
                        margin: 0 auto 1.5rem auto;
                    }
                    .welcome-info-section {
                        text-align: left;
                    }
                    .info-section-title {
                        font-size: 0.85rem;
                        font-weight: 600;
                        color: #475569;
                        margin-bottom: 0.75rem;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        text-align: center;
                    }
                    .info-chips-grid {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 8px;
                        justify-content: center;
                    }
                    .info-chip {
                        background: #ffffff;
                        border: 1px solid #e2e8f0;
                        padding: 6px 14px;
                        border-radius: 30px;
                        font-size: 0.82rem;
                        color: #475569;
                        font-weight: 500;
                        transition: all 0.2s ease;
                        cursor: pointer;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    }
                    .info-chip:hover {
                        border-color: #6366f1;
                        background: rgba(99, 102, 241, 0.05);
                        color: #4f46e5;
                        transform: translateY(-1px);
                        box-shadow: 0 4px 6px rgba(99, 102, 241, 0.08);
                    }

                    /* 입력 폼 및 전송 버튼 */
                    .agent-input-area {
                        padding: 1.25rem 1.5rem;
                        background: #ffffff;
                        border-top: 1px solid rgba(0, 0, 0, 0.05);
                    }
                    .agent-input-area form {
                        width: 100%;
                    }
                    .agent-input-area .input-group {
                        display: flex;
                        align-items: center;
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 30px;
                        padding: 4px 8px 4px 16px;
                        transition: all 0.25s ease;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
                    }
                    .agent-input-area .input-group:focus-within {
                        background: #ffffff;
                        border-color: #4f46e5;
                        box-shadow: 0 4px 15px rgba(79, 70, 229, 0.08);
                    }
                    .agent-input-field,
                    .agent-input-field.form-control {
                        border: none !important;
                        background: transparent !important;
                        box-shadow: none !important;
                        padding: 10px 0 !important;
                        font-size: 0.95rem !important;
                        color: #1e293b !important;
                        border-radius: 0 !important;
                    }
                    .agent-send-button,
                    .agent-send-button.btn {
                        border-radius: 20px !important;
                        background: linear-gradient(135deg, #6366f1, #4f46e5) !important;
                        border: none !important;
                        color: #ffffff !important;
                        font-weight: 600 !important;
                        padding: 8px 20px !important;
                        font-size: 0.85rem !important;
                        transition: all 0.2s ease !important;
                        box-shadow: 0 4px 12px rgba(79, 70, 229, 0.15) !important;
                        margin-left: 8px !important;
                    }
                    .agent-send-button:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 6px 16px rgba(79, 70, 229, 0.25) !important;
                    }
                    
                    /* 모드 선택 버튼 그룹 */
                    .agent-input-area .btn-group {
                        display: flex !important;
                        flex-wrap: nowrap !important;
                        overflow-x: auto;
                        width: 100%;
                        gap: 8px;
                        background: transparent;
                        border-radius: 0;
                        box-shadow: none;
                        scrollbar-width: none;
                    }
                    .agent-input-area .btn-group::-webkit-scrollbar {
                        display: none;
                    }
                    .agent-input-area .btn-group .btn {
                        border-radius: 30px !important;
                        border: 1px solid #e2e8f0 !important;
                        background: #ffffff !important;
                        color: #475569 !important;
                        padding: 8px 16px !important;
                        font-size: 0.85rem !important;
                        font-weight: 600 !important;
                        display: inline-flex !important;
                        align-items: center !important;
                        gap: 6px !important;
                        white-space: nowrap !important;
                        transition: all 0.2s ease !important;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02) !important;
                    }
                    .agent-input-area .btn-group .btn:hover {
                        background: #f8fafc !important;
                        color: #4f46e5 !important;
                        border-color: #cbd5e1 !important;
                        transform: translateY(-1px);
                    }
                    .agent-input-area .btn-group .btn.active,
                    .agent-input-area .btn-group .btn-primary {
                        background: linear-gradient(135deg, #6366f1, #4f46e5) !important;
                        color: #ffffff !important;
                        border-color: #4f46e5 !important;
                        box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2) !important;
                    }

                    .agent-input-area .btn-group.btn-group-sm .btn {
                        padding: 4px 12px !important;
                        font-size: 0.75rem !important;
                        border-radius: 15px !important;
                    }

                    .agent-image-model-select {
                        border-radius: 20px !important;
                        border: 1px solid #cbd5e1 !important;
                        font-size: 0.78rem !important;
                        font-weight: 600 !important;
                        color: #334155 !important;
                        background-color: #ffffff !important;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
                        cursor: pointer !important;
                        transition: all 0.2s ease !important;
                        height: 29px !important;
                        padding: 2px 28px 2px 10px !important;
                    }
                    .agent-image-model-select:hover,
                    .agent-image-model-select:focus {
                        border-color: #6366f1 !important;
                        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15) !important;
                        outline: none !important;
                    }

                    /* 이미지 모드 하단 액션 버튼 및 모델 셀렉트 한 줄 완벽 정렬 */
                    .agent-image-action-row {
                        display: flex !important;
                        justify-content: center !important;
                        align-items: center !important;
                        gap: 8px !important;
                        flex-wrap: nowrap !important;
                        width: 100% !important;
                        overflow-x: auto !important;
                        scrollbar-width: none !important;
                        margin-bottom: 0.5rem !important;
                    }
                    .agent-image-action-row::-webkit-scrollbar {
                        display: none !important;
                    }
                    .agent-image-action-row .agent-image-model-select {
                        flex: 0 0 auto !important;
                        width: auto !important;
                        margin: 0 !important;
                    }
                    .agent-image-action-row .btn-group {
                        width: auto !important;
                        display: inline-flex !important;
                        flex: 0 0 auto !important;
                        gap: 4px !important;
                        margin: 0 !important;
                    }
                    .agent-image-action-row .btn {
                        width: auto !important;
                        flex: 0 0 auto !important;
                        white-space: nowrap !important;
                    }

                    /* 오디오 파일 컨테이너 */
                    .agent-audio-container {
                        margin-top: 10px;
                        background: rgba(0, 0, 0, 0.02);
                        padding: 8px;
                        border-radius: 12px;
                        border: 1px solid rgba(0, 0, 0, 0.04);
                        width: 300px;
                        max-width: 100%;
                    }
                    
                    /* 마이크 깜빡임 애니메이션 */
                    @keyframes pulseRed {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.4; }
                    }
                    .animate-pulse {
                        animation: pulseRed 1.5s ease infinite;
                    }

                    /* 이미지 크게 보기 모달 */
                    .agent-modal-backdrop.modal-backdrop {
                        backdrop-filter: blur(8px);
                        background-color: rgba(0, 0, 0, 0.7);
                    }
                    .agent-modal-dialog {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .agent-modal-content {
                        background-color: transparent;
                        border: none;
                        box-shadow: none;
                        position: relative;
                    }
                    .agent-modal-body {
                        padding: 0;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        max-width: 90vw;
                        max-height: 90vh;
                        background-color: transparent;
                    }
                    .agent-large-image {
                        max-width: 100%;
                        max-height: 80vh;
                        object-fit: contain;
                        border-radius: 16px;
                        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
                        display: block;
                        margin: 0 auto;
                    }
                    .agent-modal-close-button {
                        position: absolute;
                        top: -20px;
                        right: -20px;
                        background-color: rgba(255, 255, 255, 0.9);
                        border-radius: 50%;
                        width: 40px;
                        height: 40px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 2rem;
                        line-height: 1;
                        color: #1e293b;
                        z-index: 1055;
                        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
                        transition: all 0.2s ease-in-out;
                    }
                    .agent-modal-close-button:hover {
                        background-color: #ef4444;
                        color: white;
                    }
                    .agent-large-image-download {
                        margin-top: 15px;
                    }
                    .agent-large-image-download a {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 10px 20px;
                        background-color: #10b981;
                        color: white;
                        border-radius: 30px;
                        text-decoration: none;
                        font-size: 0.9rem;
                        font-weight: 600;
                        transition: background-color 0.2s ease-in-out;
                        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
                    }
                    .agent-large-image-download a:hover {
                        background-color: #059669;
                    }

                    /* 모바일 반응형 미디어 쿼리 */
                    @media (max-width: 768px) {
                        .agent-container {
                            position: fixed !important;
                            top: 56px !important; /* 상단 헤더 바로 아래로 고정 */
                            left: 0 !important;
                            right: 0 !important;
                            bottom: 0 !important;
                            height: calc(100dvh - 130px) !important; /* 상단 헤더(56px) + 하단 브라우저바(56px) + 안전 이격(18px) 차감 */
                            height: calc(100vh - 130px) !important; /* fallback */
                            width: 100% !important;
                            max-width: 100% !important;
                            padding: 8px !important;
                            margin: 0 !important;
                            overflow: hidden !important;
                            box-sizing: border-box !important;
                            z-index: 99;
                        }
                        .agent-layout {
                            margin-top: 0 !important;
                            border-radius: 16px;
                            min-height: auto !important;
                            height: 100% !important;
                            width: 100% !important;
                            max-width: 100% !important;
                            overflow: hidden !important;
                        }
                        .agent-chat-card {
                            width: 100% !important;
                            max-width: 100% !important;
                            min-width: 0 !important;
                            overflow: hidden !important;
                        }
                        .agent-header {
                            padding: 0.75rem 0.75rem !important;
                            flex-wrap: wrap !important;
                            gap: 8px !important;
                        }
                        .agent-title {
                            font-size: 0.95rem !important;
                        }
                        footer {
                            display: none !important;
                        }
                        .agent-sidebar {
                            position: absolute;
                            top: 0;
                            left: 0;
                            height: 100%;
                            width: 270px;
                            z-index: 1000;
                            box-shadow: 10px 0 30px rgba(0, 0, 0, 0.15);
                            transform: translateX(0);
                            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        }
                        .agent-sidebar.collapsed {
                            transform: translateX(-100%);
                            width: 0px;
                        }
                        .agent-messages-display {
                            padding: 1rem 0.75rem !important;
                            width: 100% !important;
                            max-width: 100% !important;
                            overflow-x: hidden !important;
                        }
                        .agent-message-bubble {
                            max-width: 90%;
                            padding: 12px 16px;
                            font-size: 0.9rem;
                        }
                        .agent-input-area {
                            padding: 10px 12px 24px 12px !important; /* 하단 툴바 가림 안전패딩 보강 */
                        }
                        .agent-input-area .input-group {
                            padding: 2px 4px 2px 12px;
                        }
                        .agent-send-button,
                        .agent-send-button.btn {
                            padding: 6px 14px !important;
                        }
                        .agent-input-area .btn-group {
                            padding: 2px 0;
                        }
                        .sidebar-mobile-backdrop {
                            display: none;
                        }
                    }
                    
                    @media (max-width: 768px) {
                        .sidebar-mobile-backdrop {
                            display: block;
                            position: absolute;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            background: rgba(0, 0, 0, 0.4);
                            backdrop-filter: blur(3px);
                            -webkit-backdrop-filter: blur(3px);
                            z-index: 999;
                        }

                        /* 모바일 전용 프리미엄 스타일 */
                        .agent-message-bubble.user.agent-image-prompt-board-wrapper {
                            max-width: 92% !important;
                            padding: 12px 14px !important;
                        }
                        .prompt-board-grid {
                            grid-template-columns: 1fr !important;
                            gap: 8px !important;
                        }
                        .prompt-board-item {
                            padding: 6px 10px !important;
                        }
                        .prompt-item-value {
                            font-size: 0.8rem !important;
                        }
                        .agent-message-bubble.bot.agent-premium-image-card-wrapper {
                            max-width: 260px !important;
                        }
                        .agent-image-overlay {
                            opacity: 1 !important;
                            background: rgba(15, 23, 42, 0.25) !important;
                            backdrop-filter: none !important;
                            -webkit-backdrop-filter: none !important;
                            justify-content: flex-end !important;
                            padding: 8px !important;
                            align-items: flex-end !important;
                            pointer-events: none !important;
                        }
                        .overlay-action-btn {
                            pointer-events: auto !important;
                            width: 36px !important;
                            height: 36px !important;
                            box-shadow: 0 2px 8px rgba(0,0,0,0.2) !important;
                        }
                        .overlay-action-btn svg {
                            width: 14px !important;
                            height: 14px !important;
                        }
                        .agent-premium-modal-dialog {
                            margin: 1rem auto !important;
                            min-height: calc(100% - (1rem * 2)) !important;
                        }
                        .agent-premium-modal-content {
                            border-radius: 20px !important;
                        }
                        .agent-premium-modal-body {
                            padding: 16px !important;
                        }
                        .agent-premium-large-image {
                            max-height: 50vh !important;
                        }
                        .agent-premium-download-btn {
                            width: 100% !important;
                            justify-content: center !important;
                            padding: 10px 20px !important;
                        }
                    }

                    /* 공통/데스크톱 프리미엄 스타일 */
                    .agent-message-bubble.user.agent-image-prompt-board-wrapper {
                        background: linear-gradient(135deg, #6366f1, #4f46e5) !important;
                        border: 1px solid rgba(255, 255, 255, 0.1) !important;
                        box-shadow: 0 8px 24px rgba(79, 70, 229, 0.25) !important;
                        padding: 16px 20px !important;
                        max-width: 85%;
                    }
                    .agent-image-prompt-board {
                        width: 100%;
                    }
                    .prompt-board-header {
                        display: flex;
                        align-items: center;
                        margin-bottom: 12px;
                        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
                        padding-bottom: 8px;
                    }
                    .prompt-board-title {
                        font-size: 0.95rem;
                        font-weight: 700;
                        color: #ffffff;
                        letter-spacing: -0.01em;
                    }
                    .prompt-board-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 10px;
                    }
                    .prompt-board-item {
                        background: rgba(0, 0, 0, 0.15);
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        border-radius: 12px;
                        padding: 8px 12px;
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                        transition: all 0.2s ease;
                    }
                    .prompt-board-item:hover {
                        background: rgba(0, 0, 0, 0.25);
                        border-color: rgba(255, 255, 255, 0.15);
                        transform: translateY(-1px);
                    }
                    .prompt-item-label {
                        font-size: 0.72rem;
                        font-weight: 600;
                        color: rgba(255, 255, 255, 0.75);
                        text-transform: uppercase;
                        letter-spacing: 0.02em;
                    }
                    .prompt-item-value {
                        font-size: 0.85rem;
                        font-weight: 700;
                        color: #ffffff;
                        word-break: break-all;
                    }

                    .agent-message-bubble.bot.agent-premium-image-card-wrapper {
                        padding: 8px !important;
                        background: #ffffff !important;
                        border: 1px solid rgba(0, 0, 0, 0.08) !important;
                        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05) !important;
                        max-width: 320px;
                    }
                    .agent-premium-image-card {
                        width: 100%;
                        overflow: hidden;
                        border-radius: 14px;
                    }
                    .agent-image-wrapper {
                        position: relative;
                        width: 100%;
                        border-radius: 12px;
                        overflow: hidden;
                        aspect-ratio: auto;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                        background: #f1f5f9;
                    }
                    .agent-gallery-image {
                        width: 100%;
                        height: auto;
                        display: block;
                        object-fit: cover;
                        transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                    }
                    .agent-image-wrapper:hover .agent-gallery-image {
                        transform: scale(1.04);
                    }
                    .agent-image-overlay {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(15, 23, 42, 0.45);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 12px;
                        opacity: 0;
                        transition: opacity 0.3s ease;
                        backdrop-filter: blur(2px);
                        -webkit-backdrop-filter: blur(2px);
                    }
                    .agent-image-wrapper:hover .agent-image-overlay {
                        opacity: 1;
                    }
                    .overlay-action-btn {
                        width: 44px;
                        height: 44px;
                        border-radius: 50%;
                        background: rgba(255, 255, 255, 0.9);
                        border: none;
                        color: #0f172a;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        text-decoration: none !important;
                    }
                    .overlay-action-btn:hover {
                        background: #ffffff;
                        color: #4f46e5;
                        transform: scale(1.1);
                        box-shadow: 0 6px 16px rgba(79, 70, 229, 0.25);
                    }

                    .agent-premium-modal-backdrop.modal-backdrop {
                        background-color: rgba(10, 10, 12, 0.8) !important;
                        backdrop-filter: blur(12px) !important;
                        -webkit-backdrop-filter: blur(12px) !important;
                    }
                    .agent-premium-modal-dialog {
                        max-width: 600px !important;
                        width: 90% !important;
                        margin: 1.75rem auto !important;
                        display: flex;
                        align-items: center;
                        min-height: calc(100% - (1.75rem * 2)) !important;
                    }
                    .agent-premium-modal-content {
                        background: rgba(30, 30, 35, 0.85) !important;
                        backdrop-filter: blur(20px);
                        -webkit-backdrop-filter: blur(20px);
                        border: 1px solid rgba(255, 255, 255, 0.1) !important;
                        border-radius: 24px !important;
                        overflow: hidden;
                        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
                        width: 100%;
                    }
                    .agent-premium-modal-body {
                        padding: 24px !important;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        position: relative;
                    }
                    .agent-premium-modal-close-button {
                        position: absolute;
                        top: 16px;
                        right: 16px;
                        width: 36px;
                        height: 36px;
                        border-radius: 50%;
                        background: rgba(255, 255, 255, 0.08);
                        border: 1px solid rgba(255, 255, 255, 0.05);
                        color: rgba(255, 255, 255, 0.8);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        z-index: 10;
                    }
                    .agent-premium-modal-close-button:hover {
                        background: rgba(239, 68, 68, 0.9);
                        color: #ffffff;
                        border-color: transparent;
                        transform: rotate(90deg);
                    }
                    .agent-premium-image-container {
                        width: 100%;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        background: rgba(0, 0, 0, 0.2);
                        border-radius: 16px;
                        overflow: hidden;
                        border: 1px solid rgba(255, 255, 255, 0.05);
                        margin-top: 16px;
                    }
                    .agent-premium-large-image {
                        max-width: 100%;
                        max-height: 60vh;
                        object-fit: contain;
                        display: block;
                        transition: transform 0.3s ease;
                    }
                    .agent-premium-modal-footer {
                        width: 100%;
                        margin-top: 20px;
                        display: flex;
                        justify-content: center;
                    }
                    .agent-premium-download-btn {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 12px 28px;
                        background: linear-gradient(135deg, #6366f1, #4f46e5);
                        color: #ffffff !important;
                        font-size: 0.9rem;
                        font-weight: 600;
                        border-radius: 30px;
                        text-decoration: none !important;
                        transition: all 0.25s ease;
                        box-shadow: 0 4px 15px rgba(79, 70, 229, 0.3);
                        border: none;
                    }
                    .agent-premium-download-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(79, 70, 229, 0.45);
                        background: linear-gradient(135deg, #4f46e5, #4338ca);
                    }
                `},StartLine:2365,TargetContent:
            </style>

            <div className="agent-layout">
                {/* 1. 좌측 대화방 목록 사이드바 */}
                <div className={`agent-sidebar ${isSidebarOpen ? '' : 'collapsed'}`}>
                    <div className="sidebar-header">
                        <h6 className="sidebar-title">💬 대화방 목록</h6>
                        <div className="d-flex align-items-center gap-2">
                            <Button className="new-chat-btn" onClick={handleNewChat}>
                                <FaPlus size={10} /> 새 대화
                            </Button>
                            <button
                                className="sidebar-close-mobile-btn"
                                onClick={() => setIsSidebarOpen(false)}
                                title="대화방 목록 닫기"
                            >
                                <FaTimes size={14} />
                            </button>
                        </div>
                    </div>
                    <div className="session-list">
                        {isSessionsLoading && (
                            <div className="text-center py-4">
                                <Spinner animation="border" size="sm" variant="light" />
                            </div>
                        )}
                        {!isSessionsLoading && sessions.length === 0 && (
                            <div className="text-center text-muted small py-4">
                                대화방이 없습니다.
                            </div>
                        )}
                        {!isSessionsLoading && sessions.map((sess) => (
                            <div
                                key={sess.id}
                                className={`session-item ${currentSessionId === sess.id ? 'active' : ''}`}
                                onClick={() => {
                                    setCurrentSessionId(sess.id);
                                    if (sess.mode === 'text' || sess.mode === 'rag' || sess.mode === 'insurance') {
                                        setMode(sess.mode);
                                    }
                                    if (window.innerWidth <= 768) {
                                        setIsSidebarOpen(false);
                                    }
                                }}
                            >
                                <div className="session-title">
                                    {sess.mode === 'insurance' ? '🛡️ ' : sess.mode === 'rag' ? '⚖️ ' : '📝 '}
                                    {sess.title}
                                </div>
                                <button
                                    className="session-delete-btn"
                                    onClick={(e) => handleDeleteSession(e, sess.id)}
                                    title="대화방 삭제"
                                >
                                    <FaTrash size={10} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 모바일용 사이드바 배경 블러 백드롭 */}
                {isSidebarOpen && (
                    <div
                        className="sidebar-mobile-backdrop"
                        onClick={() => setIsSidebarOpen(false)}
                        data-html2canvas-ignore="true"
                    />
                )}

                {/* 2. 우측 메인 채팅 카드 */}
                <div className="agent-chat-card">
                    <div className="agent-header d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="sidebar-toggle-btn"
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                title={isSidebarOpen ? "사이드바 접기" : "사이드바 펼치기"}
                            >
                                <FaBars size={16} />
                            </button>
                            <h5 className="agent-title m-0">💬 와! 에이전트</h5>
                        </div>
                        {currentSessionId && (
                            <div className="d-flex align-items-center gap-2" style={{ position: 'relative' }}>
                                {messages.length > 0 && (
                                    <>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => setHeaderMenuOpen(prev => !prev)}
                                            className="d-flex align-items-center gap-1"
                                            style={{ fontSize: '0.72rem', fontWeight: 600, padding: '4px 8px' }}
                                            data-html2canvas-ignore="true"
                                        >
                                            <FaCopy size={11} /> <span className="d-none d-sm-inline">전체 </span>저장/복사
                                        </Button>
                                        {headerMenuOpen && (
                                            <div className="header-dropdown-menu" data-html2canvas-ignore="true">
                                                <button className="header-dropdown-item" onClick={handleCopyAllMarkdown}>
                                                    <FaCopy size={12} /> {allCopied ? '복사 완료! ✔' : '마크다운 복사'}
                                                </button>
                                                <button className="header-dropdown-item" onClick={handleSaveAllAsPDF}>
                                                    <FaFilePdf size={12} /> PDF로 저장
                                                </button>
                                                <button className="header-dropdown-item" onClick={handleSaveAllAsPNG}>
                                                    <FaFileImage size={12} /> 이미지로 저장
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                                <Badge bg="secondary" style={{ fontSize: '0.7rem', opacity: 0.85 }} className="d-flex align-items-center">
                                    <span className="d-none d-sm-inline">방 ID:&nbsp;</span>{currentSessionId.substring(0, 8)}...
                                </Badge>
                            </div>
                        )}
                    </div>
                    <div id="agent-messages-display" className="agent-messages-display">
                        {/* 드롭다운 외부 클릭 감지 백드롭 */}
                        {(activeMenuIndex !== null || headerMenuOpen) && (
                            <div
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 998,
                                    cursor: 'default'
                                }}
                                onClick={() => {
                                    setActiveMenuIndex(null);
                                    setHeaderMenuOpen(false);
                                }}
                                data-html2canvas-ignore="true"
                            />
                        )}
                        {/* 메시지가 없을 때 안내 메시지 */}
                        {messages.length === 0 && !isLoading && (
                            <div className="agent-welcome-card mx-auto">
                                <div className="welcome-icon-wrapper">
                                    {mode === 'insurance' ? <FaShieldAlt size={28} className="welcome-icon insurance" style={{ color: '#10b981' }} /> : mode === 'rag' ? <FaBalanceScale size={28} className="welcome-icon rag" /> : <FaRobot size={28} className="welcome-icon" />}
                                </div>
                                <h4 className="welcome-title">
                                    {mode === 'insurance' ? '와! 보험약관 RAG 전문 에이전트' : mode === 'rag' ? '와! RAG 법률·규정 전문 에이전트' : '안녕하세요! 와! 에이전트입니다'}
                                </h4>
                                <p className="welcome-subtitle">
                                    {mode === 'insurance' ? (
                                        '사용자가 개인적으로 가입한 8종의 보험약관 지식을 기반으로 보장 범위, 해약환급금, 지급 조건 등을 분석합니다.'
                                    ) : mode === 'rag' ? (
                                        '다차원 메타데이터 기반 국가 법령, 기업 규정 및 기업 보고서를 분석하여 명쾌하고 전문적인 자문을 제공합니다.'
                                    ) : (
                                        '텍스트 상담, 이미지 생성, 음성 인식 및 작곡까지 수행하는 다재다능한 AI 에이전트와 대화를 시작해 보세요.'
                                    )}
                                </p>

                                {mode === 'rag' && (
                                    <div className="welcome-laws-section mb-3" style={{ textAlign: 'center' }}>
                                        <h6 className="info-section-title" style={{ marginBottom: '6px' }}>🏛️ 상담 가능 법령 및 규정 범위</h6>
                                        <p style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: '1.6', margin: '0 auto 1.25rem auto', maxWidth: '90%' }}>
                                            헌법, 민법, 형법, 조세법(소득세·법인세·부가가치세·종부세·지방세법 등 31개 세법), 노동법(근로기준법·최저임금법·남녀고용평등법 등), 산림법, 여신금융법, 행정일반법, 재정·경제일반법, 주택·건축·도로법, 사회복지법, 판례, 기업 규정(현대카드 이용약관 등) 및 풀무원 지속가능경영보고서(통합보고서·회사소개서)
                                         </p>
                                    </div>
                                )}

                                <div className="welcome-divider" />

                                <div className="welcome-info-section">
                                    <h6 className="info-section-title">💡 {mode === 'insurance' ? '추천 보험 질문' : mode === 'rag' ? '추천 상담 질문' : '추천 대화 주제'}</h6>
                                    {mode === 'insurance' ? (
                                        <div className="info-chips-grid">
                                            <span className="info-chip" onClick={() => setInputText('DB생명 종신보험에서 중도인출 조건과 한도는 어떻게 돼?')}>💰 DB생명 중도인출</span>
                                            <span className="info-chip" onClick={() => setInputText('교보생명 나이스암치료보험의 암 보장 개시일과 지급 제한 사유를 알려줘.')}>🩺 교보생명 암 보장</span>
                                            <span className="info-chip" onClick={() => setInputText('현대해상 건강한심혈케어보험에서 심혈관 질환 진단비 지급 요건은?')}>❤️ 현대해상 심혈케어</span>
                                            <span className="info-chip" onClick={() => setInputText('한화생명 뉴V-dex 변액연금보험에서 연금 전환 신청 기간과 조건은?')}>📈 한화생명 변액연금</span>
                                        </div>
                                    ) : mode === 'rag' ? (
                                        <div className="info-chips-grid">
                                            <span className="info-chip" onClick={() => setInputText('종합소득세 신고 시 비용처리 오류로 인한 가산세 규정은?')}>💸 소득세 가산세 규정</span>
                                            <span className="info-chip" onClick={() => setInputText('근로기준법상 연차 유급휴가 발생 기준과 미사용 수당 지급 규정은?')}>💼 연차휴가 기준</span>
                                            <span className="info-chip" onClick={() => setInputText('임직원의 안전보건 관리 체계와 고객 정보 보호를 위해 풀무원이 취하고 있는 보안 정책을 알려줘.')}>🌱 풀무원 안전보건</span>
                                            <span className="info-chip" onClick={() => setInputText('임대차 계약 시 주의해야 할 민법 조항을 알려줘.')}>🏠 임대차 민법 조항</span>
                                        </div>
                                    ) : (
                                        <div className="info-chips-grid">
                                             <span className="info-chip" onClick={() => setInputText('AI 기술의 미래에 대해 어떻게 생각해?')}>🤖 AI 기술의 미래</span>
                                             <span className="info-chip" onClick={() => setInputText('재미있는 포춘쿠키 이야기 하나 해줘.')}>🥠 포춘쿠키 이야기</span>
                                             <span className="info-chip" onClick={() => setInputText('리액트(React)로 블로그를 만들 때 유용한 팁을 알려줘.')}>💻 리액트 개발 팁</span>
                                             <span className="info-chip" onClick={() => setInputText('파이썬으로 간단한 구구단 출력 코드를 짜줘.')}>🐍 파이썬 기초 코드</span>
                                         </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* 메시지 목록 렌더링 */}
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                id={`msg-bubble-${index}`}
                                className={'agent-message-bubble ' + msg.sender + (msg.sender === 'user' && msg.type === 'text' && msg.text.includes('화풍/스타일') ? ' agent-image-prompt-board-wrapper' : '') + (msg.type === 'image' ? ' agent-premium-image-card-wrapper' : '')}
                                style={activeMenuIndex === index ? { zIndex: 1005 } : {}}
                            >
                                {msg.type === 'text' && (
                                    <div className="copy-btn-container" data-html2canvas-ignore="true">
                                        <button
                                            className="copy-bubble-btn"
                                            style={{ opacity: activeMenuIndex === index ? 1 : undefined }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveMenuIndex(prev => prev === index ? null : index);
                                            }}
                                            title="대화 내용 복사/저장"
                                        >
                                            <FaCopy size={12} />
                                        </button>
                                        {activeMenuIndex === index && (
                                            <div className="copy-dropdown-menu">
                                                <button className="copy-dropdown-item" onClick={() => handleCopyMessage(msg.text, index)}>
                                                    <FaCopy size={12} /> {copiedIndex === index ? '복사 완료! ✔️' : '마크다운 복사'}
                                                </button>
                                                <button className="copy-dropdown-item" onClick={() => handleSaveAsPDF(index)}>
                                                    <FaFilePdf size={12} /> PDF로 저장
                                                </button>
                                                <button className="copy-dropdown-item" onClick={() => handleSaveAsPNG(index)}>
                                                    <FaFileImage size={12} /> 이미지로 저장
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {msg.type === 'text' && (
                                    msg.sender === 'user' && msg.text.includes('화풍/스타일') ? (
                                        <div className="agent-image-prompt-board">
                                            <div className="prompt-board-header">
                                                <span className="prompt-board-title">🎲 이미지 생성 프롬프트</span>
                                            </div>
                                            <div className="prompt-board-grid">
                                                {parseImagePrompt(msg.text).map((item, idx) => (
                                                    <div key={idx} className="prompt-board-item">
                                                        <div className="prompt-item-label">
                                                            {categoryEmojis[item.label] || '✨'} {item.label}
                                                        </div>
                                                        <div className="prompt-item-value">{item.value}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                code: CodeBlock
                                            }}
                                        >
                                            {msg.text}
                                        </ReactMarkdown>
                                    )
                                )}
                                {msg.type === 'image' && (
                                    <div className="agent-premium-image-card">
                                        <div className="agent-image-wrapper">
                                            <img
                                                src={msg.text}
                                                alt="생성된 이미지"
                                                className="agent-gallery-image"
                                                onLoad={scrollToBottom}
                                            />
                                            <div className="agent-image-overlay">
                                                <button
                                                    className="overlay-action-btn expand"
                                                    onClick={() => handleImageClick(msg.text, msg.fileName)}
                                                    title="확대 보기"
                                                >
                                                    <FaEye size={18} />
                                                </button>
                                                <a
                                                    href={msg.text}
                                                    download={msg.fileName}
                                                    className="overlay-action-btn download"
                                                    title="이미지 다운로드"
                                                >
                                                    <FaArrowDown size={16} />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {msg.type === 'audio' && (
                                    <div className="agent-audio-container">
                                        <audio controls src={msg.text} className="w-100" />
                                    </div>
                                )}
                                {/* 소요 시간 표시 */}
                                {msg.duration && (
                                    <div className="text-end mt-1" style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                        <FaClock size={10} className="me-1" />{msg.duration}s
                                    </div>
                                )}
                            </div>
                        ))}
                        {/* 작곡 모드일 때 작성 중인 내용 실시간 미리보기 (대화창에 붙여주기) */}
                        {mode === 'music' && (musicComposition.lyricsSegments.length > 0 || musicComposition.tags.length > 0) && (
                            <div className="agent-message-bubble user music-draft-bubble">
                                <div className="music-draft-content">
                                    <h6 className="fw-bold mb-2">🎵 작곡 중...</h6>

                                    {/* 1. 태그 미리보기 */}
                                    {musicComposition.tags.length > 0 && (
                                        <div className="mb-3 d-flex flex-wrap gap-1">
                                            {musicComposition.tags.map((tag, idx) => (
                                                <Badge key={idx} bg="info" className="d-flex align-items-center gap-1 p-2">
                                                    {tag.text}
                                                    <FaTimes
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleRemoveTag(tag.value)}
                                                        title="삭제"
                                                    />
                                                </Badge>
                                            ))}
                                        </div>
                                    )}

                                    {/* 2. 가사 미리보기 */}
                                    {musicComposition.lyricsSegments.length > 0 && (
                                        <div className="lyrics-preview-container d-flex flex-column gap-2">
                                            {musicComposition.lyricsSegments.map((segment, idx) => (
                                                <div key={idx} className="lyric-segment-box border rounded p-2 position-relative bg-light">
                                                    {/* 이동 및 닫기 버튼 그룹 (오른쪽 상단) */}
                                                    <div className="position-absolute top-0 end-0 p-1 d-flex gap-1 align-items-center">
                                                        {/* 위로 이동 */}
                                                        {idx > 0 && (
                                                            <FaArrowUp
                                                                style={{ cursor: 'pointer', color: '#6c757d', fontSize: '0.8rem' }}
                                                                onClick={() => handleMoveLyric(idx, 'up')}
                                                                title="위로 이동"
                                                            />
                                                        )}
                                                        {/* 아래로 이동 */}
                                                        {idx < musicComposition.lyricsSegments.length - 1 && (
                                                            <FaArrowDown
                                                                style={{ cursor: 'pointer', color: '#6c757d', fontSize: '0.8rem' }}
                                                                onClick={() => handleMoveLyric(idx, 'down')}
                                                                title="아래로 이동"
                                                            />
                                                        )}
                                                        {/* 삭제 */}
                                                        <FaTimes
                                                            style={{ cursor: 'pointer', color: '#dc3545', marginLeft: '4px' }}
                                                            onClick={() => handleRemoveLyric(idx)}
                                                            title="삭제"
                                                        />
                                                    </div>

                                                    {/* 가사 구조 라벨 */}
                                                    <Badge bg="secondary" className="mb-1">{segment.structure}</Badge>

                                                    {/* 가사 내용 */}
                                                    <div className="lyric-text" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em', color: '#000' }}>
                                                        {segment.text}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 로딩 중일 때 스피너 표시 */}
                        {isLoading && (
                            <div className="agent-message-bubble bot is-loading">
                                <div className="d-flex align-items-center">
                                    <Spinner animation="grow" size="sm" className="me-2" />
                                    {mode === 'voice' ? '음성을 생성하고 있어요...' : '응답을 기다리는 중...'} {processingTime > 0 && `(${processingTime.toFixed(1)}s)`}
                                </div>
                            </div>
                        )}
                        {/* 오류 메시지 표시 */}
                        {error && <Alert variant="danger" className="mt-2 text-center">{error}</Alert>}
                        {/* 스크롤 위치를 잡아주는 빈 div */}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* 입력 폼 */}
                    <div className="agent-input-area">
                        {/* 모드 선택 버튼 그룹 */}
                        <div className="d-flex justify-content-center mb-3">
                            <ButtonGroup>
                                <Button
                                    variant={mode === 'text' ? "primary" : "outline-secondary"}
                                    onClick={() => setMode('text')}
                                >
                                    <FaFont /> 텍스트
                                </Button>
                                <Button
                                    variant={mode === 'rag' ? "primary" : "outline-secondary"}
                                    onClick={() => setMode('rag')}
                                >
                                    <FaBalanceScale /> 전문상담
                                </Button>
                                {isLoggedIn && user?.email === 'wajok@wajok.com' && (
                                    <Button
                                        variant={mode === 'insurance' ? "primary" : "outline-secondary"}
                                        onClick={() => setMode('insurance')}
                                    >
                                        <FaShieldAlt /> 보험상담
                                    </Button>
                                )}
                                <Button
                                    variant={mode === 'image' ? "primary" : "outline-secondary"}
                                    onClick={() => setMode('image')}
                                >
                                    <FaImage /> 이미지
                                </Button>
                                <Button
                                    variant={mode === 'voice' ? "primary" : "outline-secondary"}
                                    onClick={() => setMode('voice')}
                                >
                                    <FaHeadphones /> 음성
                                </Button>
                                {/* 작곡 모드 버튼 추가 */}
                                <Button
                                    variant={mode === 'music' ? "primary" : "outline-secondary"}
                                    onClick={() => setMode('music')}
                                >
                                    <FaMusic /> 작곡
                                </Button>
                            </ButtonGroup>
                        </div>

                        {/* 이미지 모드일 때 모델 선택 및 텍스트/무작위 선택 버튼 그룹 (한 줄 배치) */}
                        {mode === 'image' && (
                            <div className="agent-image-action-row">
                                {/* 맨 왼쪽: 이미지 모델 선택 셀렉트 박스 */}
                                <Form.Select
                                    size="sm"
                                    value={imageModel}
                                    onChange={(e) => setImageModel(e.target.value)}
                                    className="agent-image-model-select"
                                    title="이미지 생성 모델 선택"
                                >
                                    <option value="image">⚡ 번개 뚝딱이</option>
                                    <option value="imageQwen">✨ 디테일 끝판왕</option>
                                </Form.Select>

                                <ButtonGroup size="sm">
                                    <Button
                                        variant={imageInputMode === 'text' ? "success" : "outline-success"}
                                        onClick={() => handleImageModeChange('text')}
                                    >
                                        텍스트 📝
                                    </Button>
                                    <Button
                                        variant={imageInputMode === 'random' ? "success" : "outline-success"}
                                        onClick={() => handleImageModeChange('random')}
                                    >
                                        무작위 🎲
                                    </Button>
                                    {/* 쏘세요! 버튼 추가 */}
                                    {imageInputMode === 'random' && (
                                        <>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={handleShootRoulette}
                                                disabled={isLoading}
                                                className="d-flex align-items-center gap-1 fw-bold"
                                                style={{ background: 'linear-gradient(45deg, #FF416C, #FF4B2B)', border: 'none' }}
                                            >
                                                <FaBullseye className={isLoading ? "fa-spin" : ""} />
                                                {isLoading ? "조준 중..." : "쏘세요!"}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setIsRandomImageExpanded(!isRandomImageExpanded)}
                                                title={isRandomImageExpanded ? "옵션 숨기기" : "옵션 보이기"}
                                            >
                                                {isRandomImageExpanded ? <FaEyeSlash /> : <FaEye />} {isRandomImageExpanded ? '숨기기' : '보이기'}
                                            </Button>
                                        </>
                                    )}
                                </ButtonGroup>
                            </div>
                        )}

                        {/* 이미지 모드이고 'random' 일 때 셀렉트 박스 렌더링 */}
                        {mode === 'image' && imageInputMode === 'random' && isRandomImageExpanded && (
                            <div className="mb-3">
                                <div className="row g-2">
                                    {categoryOrder.map((category) => (
                                        <div key={category} className="col-6 col-md-4">
                                            <Form.Group>
                                                <Form.Label className="small text-muted mb-1">{categoryLabels[category]}</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={selectedOptions[category]}
                                                    onChange={(e) => handleOptionChange(category, e.target.value)}
                                                >
                                                    {/* 무작위 옵션 제거 */}
                                                    <option value="None">사용안함</option>
                                                    <option value="Prompt">직접입력</option>
                                                    {promptOptions[category]?.map((opt, idx) => (
                                                        <option key={idx} value={idx}>{opt}</option>
                                                    ))}
                                                </Form.Select>
                                                {/* 직접 입력 선택 시 텍스트 입력창 표시 */}
                                                {selectedOptions[category] === 'Prompt' && (
                                                    <Form.Control
                                                        type="text"
                                                        size="sm"
                                                        className="mt-1"
                                                        placeholder="직접 입력..."
                                                        value={manualInputs[category] || ''}
                                                        onChange={(e) => handleManualInputChange(category, e.target.value)}
                                                    />
                                                )}
                                            </Form.Group>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 작곡 모드 UI */}
                        {mode === 'music' && (
                            <div className="music-composer-area mb-3 p-2 border rounded bg-light">
                                {/* 1. 가사 입력 영역 */}
                                <Form.Group className="mb-2">
                                    <Form.Label className="small fw-bold">가사 구조 & 재생 시간 & BPM</Form.Label>
                                    {/* 가사 구조 선택 */}
                                    <div className="d-flex gap-2 mb-3">
                                        <Form.Select
                                            size="sm"
                                            value={lyricInputState.structure}
                                            onChange={(e) => {
                                                e.target.blur();
                                                const val = e.target.value;
                                                if (val === '가사생성') {
                                                    setShowLyricsModal(true);
                                                    return;
                                                }
                                                setLyricInputState({ structure: val, text: '' });
                                            }}
                                            style={{ width: '150px' }}
                                        >
                                            <option value="선택">선택</option>
                                            <option value="가사생성">가사생성</option>
                                            <option value="직접입력">직접입력</option>
                                            <option value="[intro]">[intro]</option>
                                            <option value="[verse]">[verse]</option>
                                            <option value="[chorus]">[chorus]</option>
                                            <option value="[bridge]">[bridge]</option>
                                            <option value="[outro]">[outro]</option>
                                        </Form.Select>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleAddLyric}
                                            disabled={lyricInputState.structure === '선택'}
                                        >
                                            <FaPlus /> {lyricInputState.structure === '직접입력' ? '일괄 추가' : '추가'}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setIsComposerExpanded(!isComposerExpanded)}
                                            title={isComposerExpanded ? "설정 숨기기" : "설정 보이기"}
                                        >
                                            {isComposerExpanded ? <FaEyeSlash /> : <FaEye />} {isComposerExpanded ? '숨기기' : '보이기'}
                                        </Button>
                                    </div>
                                </Form.Group>

                                {isComposerExpanded && (
                                    <div className="mt-2">
                                        {/* 가사 입력창 (여기로 이동) */}
                                        {lyricInputState.structure !== '선택' && (
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small text-muted">{lyricInputState.structure} 가사 입력</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={lyricInputState.structure === '직접입력' ? 6 : 3}
                                                    placeholder={lyricInputState.structure === '직접입력' ? LYRIC_PLACEHOLDER : "가사를 입력하세요..."}
                                                    value={lyricInputState.text}
                                                    onChange={(e) => setLyricInputState({ ...lyricInputState, text: e.target.value })}
                                                />
                                            </Form.Group>
                                        )}

                                        {/* Duration Slider */}
                                        <div className="mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="small text-muted" style={{ whiteSpace: 'nowrap', minWidth: '80px' }}>⏱️ {musicComposition.duration}s</span>
                                                <Form.Range
                                                    min={60}
                                                    max={240}
                                                    step={1}
                                                    value={musicComposition.duration}
                                                    onChange={(e) => setMusicComposition(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                                                    style={{ flexGrow: 1 }}
                                                />
                                            </div>
                                        </div>

                                        {/* BPM Slider */}
                                        <div className="mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="small text-muted" style={{ whiteSpace: 'nowrap', minWidth: '80px' }}>🎵 {musicComposition.bpm} BPM</span>
                                                <Form.Range
                                                    min={40}
                                                    max={200}
                                                    step={1}
                                                    value={musicComposition.bpm}
                                                    onChange={(e) => setMusicComposition(prev => ({ ...prev, bpm: parseInt(e.target.value) }))}
                                                    style={{ flexGrow: 1 }}
                                                />
                                            </div>
                                        </div>


                                        {/* 2. 음악 태그 선택 영역 (장르/보컬/악기) - 간단한 예시 구현 */}
                                        <hr className="my-2" />
                                        <Form.Label className="small fw-bold">음악 스타일 & 언어</Form.Label>
                                        <Row className="g-2 mb-2">
                                            {/* 언어 선택 추가 */}
                                            <Col xs={6} md={3}>
                                                <Form.Select
                                                    size="sm"
                                                    value={musicComposition.language}
                                                    onChange={(e) => {
                                                        setMusicComposition(prev => ({ ...prev, language: e.target.value }));
                                                        e.target.blur();
                                                    }}
                                                    style={{ backgroundColor: '#f0f8ff' }}
                                                >
                                                    <option value="Korean">한국어</option>
                                                    <option value="English">영어</option>
                                                    <option value="Chinese">중국어</option>
                                                    <option value="Russian">러시아어</option>
                                                    <option value="Spanish">스페인어</option>
                                                    <option value="Japanese">일본어</option>
                                                    <option value="German">독일어</option>
                                                    <option value="French">프랑스어</option>
                                                    <option value="Portuguese">포르투갈어</option>
                                                    <option value="Italian">이탈리아어</option>
                                                </Form.Select>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Select
                                                    size="sm"
                                                    value={tagInputState.genre}
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleAddTag(e.target.value, e.target.options[e.target.selectedIndex].text, 'genre');
                                                        }
                                                        e.target.blur();
                                                    }}
                                                >
                                                    <option value="">장르 선택</option>
                                                    {musicOptions.genres.map((opt, idx) => (
                                                        <option key={idx} value={opt.value}>{opt.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Select
                                                    size="sm"
                                                    value={tagInputState.mood}
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleAddTag(e.target.value, e.target.options[e.target.selectedIndex].text, 'mood');
                                                        }
                                                        e.target.blur();
                                                    }}
                                                >
                                                    <option value="">분위기 선택</option>
                                                    {musicOptions.moods.map((opt, idx) => (
                                                        <option key={idx} value={opt.value}>{opt.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Select
                                                    size="sm"
                                                    value={tagInputState.vocal}
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleAddTag(e.target.value, e.target.options[e.target.selectedIndex].text, 'vocal');
                                                        }
                                                        e.target.blur();
                                                    }}
                                                >
                                                    <option value="">보컬 선택</option>
                                                    {musicOptions.vocals.map((opt, idx) => (
                                                        <option key={idx} value={opt.value}>{opt.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Select
                                                    size="sm"
                                                    value={tagInputState.instrument}
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleAddTag(e.target.value, e.target.options[e.target.selectedIndex].text, 'instrument');
                                                        }
                                                        e.target.blur();
                                                    }}
                                                >
                                                    <option value="">악기 선택</option>
                                                    {musicOptions.instruments.map((opt, idx) => (
                                                        <option key={idx} value={opt.value}>{opt.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 입력 컨트롤 영역 */}
                        <Form onSubmit={handleSendMessage}>
                            <InputGroup>
                                {mode === 'voice' ? (
                                    // 음성 모드 UI
                                    <div className="d-flex w-100 justify-content-center align-items-center gap-3">
                                        {isRecording && (
                                            <span className="text-danger fw-bold animate-pulse">
                                                🔴 {formatTime(recordingTime)}
                                            </span>
                                        )}

                                        {/* 파일 업로드 버튼 (녹음 중이 아닐 때만 표시) */}
                                        {!isRecording && (
                                            <>
                                                <input
                                                    type="file"
                                                    accept="audio/*"
                                                    style={{ display: 'none' }}
                                                    ref={fileInputRef}
                                                    onChange={handleFileUpload}
                                                />
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => fileInputRef.current.click()}
                                                    className="rounded-circle p-3 d-flex align-items-center justify-content-center"
                                                    style={{ width: '50px', height: '50px' }}
                                                    disabled={isLoading}
                                                    title="오디오 파일 업로드"
                                                >
                                                    <FaFolderOpen size={20} />
                                                </Button>
                                            </>
                                        )}

                                        <Button
                                            variant={isRecording ? "danger" : "primary"}
                                            onClick={handleMicClick}
                                            className="rounded-circle p-3 d-flex align-items-center justify-content-center"
                                            style={{ width: '60px', height: '60px' }}
                                            disabled={isLoading}
                                        >
                                            {isRecording ? <FaStop size={24} /> : <FaMicrophone size={24} />}
                                        </Button>

                                        {/* 녹음 취소 버튼 (녹음 중일 때만 표시) */}
                                        {isRecording && (
                                            <Button
                                                variant="outline-danger"
                                                onClick={handleCancelRecording}
                                                className="rounded-circle p-3 d-flex align-items-center justify-content-center"
                                                style={{ width: '50px', height: '50px' }}
                                                title="녹음 취소"
                                            >
                                                <FaTimes size={20} />
                                            </Button>
                                        )}

                                        {isRecording && <span className="text-muted ms-2">녹음 중...</span>}
                                    </div>
                                ) : (
                                    // 텍스트/이미지 모드 UI
                                    <>
                                        <Form.Control
                                            ref={inputRef}
                                            as="textarea"
                                            rows={1}
                                            placeholder={
                                                mode === 'music' ? "작곡 모드입니다. 위에서 가사와 태그를 입력해주세요." :
                                                    mode === 'image' ? (imageInputMode === 'random' ? "위 옵션을 선택하여 이미지를 생성하세요." : "생성할 이미지에 대한 프롬프트를 입력하세요...") :
                                                        mode === 'rag' ? "법령 및 규정에 대해 질문을 입력하세요..." :
                                                            "무엇이든 물어보세요..."
                                            }
                                            value={inputText}
                                            onChange={(e) => setInputText(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    if (e.nativeEvent.isComposing) return; // 한글 입력 완료 시 엔터로 전송되는 현상 방지
                                                    e.preventDefault();
                                                    handleSendMessage(e);
                                                }
                                            }}
                                            disabled={isLoading || (mode === 'image' && imageInputMode === 'random') || mode === 'music'} // 랜덤/작곡 모드일 때 비활성화
                                            className="agent-input-field"
                                            style={{ resize: 'none', minHeight: '38px', maxHeight: '180px', overflowY: 'auto', scrollbarWidth: 'none' }}
                                        />
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={isLoading} // 텍스트 모드 활성화
                                            className="agent-send-button"
                                        >
                                            {isLoading ? <Spinner animation="border" size="sm" /> : (mode === 'rag' ? '상담 요청' : '전송')}
                                        </Button>
                                    </>
                                )}
                            </InputGroup>
                        </Form>
                    </div>
                </div>
            </div>

            {/* 이미지 크게 보기 모달 */}
            <Modal
                show={showLargeImage}
                onHide={handleCloseLargeImage}
                centered
                dialogClassName="agent-premium-modal-dialog"
                contentClassName="agent-premium-modal-content"
                backdropClassName="agent-premium-modal-backdrop"
            >
                <Modal.Body className="agent-premium-modal-body">
                    <button
                        className="agent-premium-modal-close-button"
                        onClick={handleCloseLargeImage}
                        title="닫기"
                    >
                        <FaTimes size={18} />
                    </button>
                    <div className="agent-premium-image-container">
                        <img src={currentImage.src} alt="확대된 이미지" className="agent-premium-large-image" />
                    </div>
                    <div className="agent-premium-modal-footer">
                        <a href={currentImage.src} download={currentImage.fileName} className="agent-premium-download-btn" title="이미지 다운로드">
                            <FaArrowDown size={14} /> 이미지 다운로드
                        </a>
                    </div>
                </Modal.Body>
            </Modal>

            {/* 가사 생성 모달 */}
            <Modal show={showLyricsModal} onHide={handleCloseLyricsModal} centered contentClassName="lyric-modal-content">
                <Modal.Header closeButton className="lyric-modal-header">
                    <Modal.Title>AI 가사 생성 🎵</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <style>
                        {`
                            .lyric-gen-textarea::placeholder {
                                color: #dc3545 !important;
                                opacity: 1;
                                font-weight: bold;
                            }
                            .lyric-modal-content {
                                border-radius: 20px !important;
                                box-shadow: 0 15px 35px rgba(0,0,0,0.2) !important;
                                border: 1px solid rgba(255, 255, 255, 0.5) !important;
                                overflow: hidden;
                                background-color: rgba(255, 255, 255, 0.85) !important;
                                backdrop-filter: blur(10px) !important;
                            }
                            .lyric-modal-header {
                                background-color: rgba(255, 255, 255, 0.6) !important;
                                border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                            }
                        `}
                    </style>
                    <Form.Group className="d-flex gap-2 mb-3">
                        <Form.Control
                            type="text"
                            placeholder="가사의 주제와 내용을 입력해주세요 (예: 힘겨운 출근길)"
                            value={lyricsPrompt}
                            onChange={(e) => setLyricsPrompt(e.target.value)}
                            disabled={isGeneratingLyrics}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isGeneratingLyrics) handleGenerateLyrics();
                            }}
                        />
                        <Button
                            variant="primary"
                            onClick={handleGenerateLyrics}
                            disabled={isGeneratingLyrics}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {isGeneratingLyrics ? <Spinner size="sm" animation="border" /> : '생성'}
                        </Button>
                    </Form.Group>

                    <Form.Control
                        as="textarea"
                        rows={12}
                        className="lyric-gen-textarea"
                        value={generatedLyrics}
                        onChange={(e) => setGeneratedLyrics(e.target.value)}
                        placeholder="재생 시간과 BPM설정 및 장르 선택을 미리 해주세요."
                        disabled={generatedLyrics.length === 0 || isGeneratingLyrics}
                    />

                    <div className="text-center mt-3">
                        <Button
                            variant="success"
                            onClick={handleApplyGeneratedLyrics}
                            disabled={!generatedLyrics}
                        >
                            적용
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Container >
    );
};

export { Agent };
